import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import SiteAlertBullet from '../components/SiteAlertBullet';
import useAPI from '../hooks/useAPI';
import { getProjects, analyzeProject, getTechnicianKPIs, getTechnicians, getUsers, assignRobotToTechnician, getManagerAssignments, listAssignments, getClosedTickets, getWeeklyTrends, getRcaRefreshStatus, forceRcaRefresh, robotCommand, robotBatteryRefresh, robotBatteryLiveReport, robotBatteryStore, robotBatteryHistory } from '../services/api';
import SeverityBadge from '../components/SeverityBadge';

// v1.10.11 — labels for return reason_code values surfaced on the
// "Fail to fix" chip when a tech bounces a robot back unfixed.
const REASON_CODE_LABEL = {
  no_access: 'No access',
  need_special_tool: 'Needs special tool',
  no_spare: 'No spare part',
  no_time: 'No time',
  not_on_site: 'Not on site',
  other: 'Other',
  eod_unfinished: 'EOD unfinished',
};

const ALL_ISRAEL = '__all_israel__';

// v1.10.2 — aggregated spare-parts card. Counts each part across the
// currently-on-error robots' parts_to_bring lists, excluding generic items
// (tools, tape measure). Replaces the 'Technicians' tile.
function SparePartsCard({ robots }) {
  const aggregated = (function () {
    const SKIP = new Set(['tools', 'tape_measure', 'flashlight', '']);
    const counts = {};
    for (const r of (robots || [])) {
      if (!r.currently_on_error) continue;
      for (const p of (r.parts_to_bring || [])) {
        const name = String(p || '').trim().toLowerCase();
        if (SKIP.has(name)) continue;
        counts[name] = (counts[name] || 0) + 1;
      }
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();
  if (!aggregated.length) {
    return (
      <div style={{
        background: '#fff', borderRadius: 8, padding: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <div style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      marginBottom: 6 }}>
          Spare parts required for today
        </div>
        <div style={{ fontSize: 13, color: '#95a5a6' }}>
          None — no robots currently on error.
        </div>
      </div>
    );
  }
  return (
    <div style={{
      background: '#fff', borderRadius: 8, padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      <div style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    marginBottom: 8 }}>
        Spare parts required for today
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {aggregated.map(([name, n]) => (
          <span key={name} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 4,
            background: '#f1f5f9', color: '#1e293b',
            fontSize: 13, fontWeight: 500,
          }}>
            <span style={{ fontWeight: 700, color: '#0284c7' }}>{n}</span>
            <span>×</span>
            <span>{name}</span>
          </span>
        ))}
      </div>
    </div>
  );
}


// --- Error code Hebrew names for display ---
const ERROR_NAMES_HE = {
  DRIVE_PEAK_LOAD_HALT: 'עצירת עומס שיא מוטור נסיעה',
  DRIVE_STALL_HALT: 'עצירת תקיעה מוטור נסיעה',
  DRIVE_HIGH_LOAD_HALT: 'עצירת עומס גבוה מוטור נסיעה',
  DRIVE_PEAK_LOAD_GO_HOME: 'חזרה הביתה - עומס שיא',
  DRIVE_STALL_GO_HOME: 'חזרה הביתה - תקיעה',
  DRIVE_HIGH_LOAD_GO_HOME: 'חזרה הביתה - עומס גבוה',
  DRIVE_MOTOR_NOT_ROTATING: 'מוטור נסיעה לא מסתובב',
  CLEAN_STALL_HALT: 'עצירת תקיעה מוטור ניקוי',
  CLEAN_MOTOR_NOT_ROTATING: 'מוטור ניקוי לא מסתובב',
  CLEAN_OVERCURRENT_HALT: 'עצירת זרם יתר ניקוי',
  BATTERY_VOLTAGE_LOW: 'מתח סוללה נמוך',
  BATTERY_VOLTAGE_CRITICAL: 'מתח סוללה קריטי',
  PCB_COMMUNICATION_ERROR: 'שגיאת תקשורת כרטיס',
  NO_EP_REPLY: 'אין תשובה מנקודת קצה',
  HOME_FLAG_PROBLEM: 'בעיית דגל בית',
  FAR_END_FLAG_PROBLEM: 'בעיית דגל קצה',
  DRIVE_PEAK_LOAD_OBSTACLE: 'מכשול - עומס שיא נסיעה',
  DRIVE_STALL_OBSTACLE: 'מכשול - תקיעה נסיעה',
  CLEAN_STALL_OBSTACLE: 'מכשול - תקיעה ניקוי',
  RESET_WHILE_ACTIVE: 'איפוס תוך כדי פעולה',
  MULTIPLE_RESETS: 'איפוסים מרובים',
  NOT_ENOUGH_BATTERY_OPTIMIZE: 'סוללה חלשה - אופטימיזציה',
  BATTERY_EMPTY: 'סוללה ריקה',
  TEMPERATURE_TOO_HIGH: 'טמפרטורה גבוהה מדי',
  CLEAN_MOTOR_RPM_TOO_LOW: 'סל"ד מוטור ניקוי נמוך',
  CLEAN_HIGH_LOAD_HALT: 'עצירת עומס גבוה ניקוי',
  DRIVE_PEAK_LOAD_GO_HOME: 'חזרה הביתה - עומס שיא',
  DRIVE_HIGH_LOAD_GO_HOME: 'חזרה הביתה - עומס גבוה',
  CLEAN_PEAK_LOAD_HALT: 'עצירת עומס שיא ניקוי',
  CLEAN_STALL_GO_HOME: 'חזרה הביתה - תקיעה ניקוי',
  NOT_ENOUGH_BATTERY: 'סוללה לא מספיקה',
  NOT_ENOUGH_BATTERY_GO_BACK: 'סוללה חלשה - חזרה',
  WIND_TOO_STRONG: 'רוח חזקה מדי',
  TIMEOUT_REACHED: 'חריגת זמן',
  CLEAN_MOTOR_ERROR_STAY_HOME: 'תקלת מוטור ניקוי - נשאר בבית',
  DRIVE_MOTOR_ERROR_STAY_HOME: 'תקלת מוטור נסיעה - נשאר בבית',
  CLEAN_PEAK_LOAD_GO_HOME: 'חזרה הביתה - עומס שיא ניקוי',
  CLEAN_HIGH_LOAD_GO_HOME: 'חזרה הביתה - עומס גבוה ניקוי',
  PCB_OVER_TEMPERATURE: 'חימום יתר כרטיס PCB',
  BATTERY_OVER_TEMPERATURE: 'חימום יתר סוללה',
};

function errorDisplayName(code) {
  return ERROR_NAMES_HE[code] || humanizeCode(code) || '';
}

/** Format time as relative: "3h ago", "2d ago" */
function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/** Format Drive error with location on row (locationOnRow is in mm, display in m) */
// Acronyms to keep uppercase when humanising error codes for display.
// Mirrors the backend _RCA_ACRONYMS list.
const RCA_ACRONYMS = new Set([
  'EP','RPM','PCB','BLE','RTC','RPI','FW','BL','HW','SW',
  'ID','MCU','UUID','BMS','AC','DC','MPPT','EOL','SOC','OS',
  'BLDC','PWM','CBC','MAC','USB','GPS','IP','API',
]);

function humanizeCode(code) {
  if (!code) return '';
  return code.split('_')
    .filter(Boolean)
    .map(t => RCA_ACRONYMS.has(t) ? t
            : /^\d+$/.test(t) ? t
            : t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())
    .join(' ');
}

/** Format Drive error with location on row.
 *  locationOnRow is in encoder TICKS (signed). Conversion per spec §16.1:
 *    meters = ticks * π * 100 / 612 / 1000   (≈ 0.0005132 m/tick)
 *  Sign is the cycle-start-mode marker; we preserve it so the popup reads
 *  e.g. "Drive Stall Halt (-45.4m)" — matching the verdict's "40m" zone bucket.
 *  v1.10 BUG A fix (2026-05-02): was incorrectly dividing ticks by 1000
 *  ("locationOnRow is in mm" — wrong; values were ~2× off everywhere). */
function errorWithLocation(code, row, locationOnRow) {
  const name = humanizeCode(code) || '?';
  if (code?.startsWith('DRIVE') && locationOnRow != null) {
    const TICK_TO_M = Math.PI * 100 / 612 / 1000;  // ≈ 0.0005132
    const meters = (locationOnRow * TICK_TO_M).toFixed(1);
    return `${name} (${meters}m)`;
  }
  return name;
}

/**
 * Colored badge for robot current status (error, parking, charging, etc.).
 */
const STATUS_STYLES = {
  error:                { bg: '#d63031', fg: '#fff', label: 'ON ERROR' },
  parking:              { bg: '#dfe6e9', fg: '#2d3436', label: 'PARKING' },
  charging:             { bg: '#0984e3', fg: '#fff', label: 'CHARGING' },
  'finished-cleaning':  { bg: '#00b894', fg: '#fff', label: 'FINISHED' },
  'not-cleaning':       { bg: '#b2bec3', fg: '#fff', label: 'NOT CLEANING' },
  idle:                 { bg: '#95a5a6', fg: '#fff', label: 'IDLE' },
  'drive-forward':      { bg: '#6c5ce7', fg: '#fff', label: 'DRIVE FWD' },
  'drive-backwards':    { bg: '#a29bfe', fg: '#fff', label: 'DRIVE BWD' },
  cleaning:             { bg: '#00cec9', fg: '#fff', label: 'CLEANING' },
  offline:              { bg: '#2d3436', fg: '#fff', label: 'OFFLINE' },
  repeater_offline:     { bg: '#d63031', fg: '#fff', label: 'REPEATER OFFLINE' },
  unknown:              { bg: '#ecf0f1', fg: '#636e72', label: 'UNKNOWN' },
};
/**
 * Two side-by-side boxes showing the top rising and improving error codes
 * for the currently-selected project. Data comes from the shared weekly
 * trends cache (same source the Errors Trend page uses).
 */
function ProjectTrendsRow({ projectName, trendsData }) {
  const { rising, improving } = useMemo(() => {
    const rows = (trendsData?.by_project || [])
      .filter(r => r.project === projectName);
    const rising = rows
      .filter(r => r.growth_pct > 0)
      .sort((a, b) => b.errors_last_7d - a.errors_last_7d)
      .slice(0, 4);
    const improving = rows
      .filter(r => r.growth_pct < 0)
      .sort((a, b) => b.errors_prior_30d - a.errors_prior_30d)
      .slice(0, 3);
    return { rising, improving };
  }, [trendsData, projectName]);

  return (
    <div style={trendRowStyles.row}>
      <div style={{ ...trendRowStyles.card, borderTop: '4px solid #d63031' }}>
        <div style={trendRowStyles.header}>
          <span style={{ color: '#d63031' }}>▲</span> Top Rising Errors
          <span style={trendRowStyles.sub}>this project</span>
        </div>
        {rising.length === 0 ? (
          <div style={trendRowStyles.empty}>No rising trends this week.</div>
        ) : (
          <ul style={trendRowStyles.list}>
            {rising.map((r, i) => (
              <li key={i} style={trendRowStyles.item}>
                <span style={trendRowStyles.code}>{r.error_code}</span>
                <span style={trendRowStyles.rate}>
                  {Math.round(r.rate_last_7d)}/day
                  <span style={trendRowStyles.prior}>
                    &nbsp;was {Math.round(r.rate_prior_30d)}/day
                  </span>
                </span>
                <span style={{ ...trendRowStyles.growth, color: '#d63031' }}>
                  +{Math.round(r.growth_pct)}%
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ ...trendRowStyles.card, borderTop: '4px solid #00b894' }}>
        <div style={trendRowStyles.header}>
          <span style={{ color: '#00b894' }}>▼</span> Top Improving Errors
          <span style={trendRowStyles.sub}>this project</span>
        </div>
        {improving.length === 0 ? (
          <div style={trendRowStyles.empty}>No improvements this week.</div>
        ) : (
          <ul style={trendRowStyles.list}>
            {improving.map((r, i) => (
              <li key={i} style={trendRowStyles.item}>
                <span style={trendRowStyles.code}>{r.error_code}</span>
                <span style={trendRowStyles.rate}>
                  {Math.round(r.rate_last_7d)}/day
                  <span style={trendRowStyles.prior}>
                    &nbsp;was {Math.round(r.rate_prior_30d)}/day
                  </span>
                </span>
                <span style={{ ...trendRowStyles.growth, color: '#00b894' }}>
                  {Math.round(r.growth_pct)}%
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const trendRowStyles = {
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 16,
  },
  card: {
    background: '#fff',
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  header: {
    fontSize: 13,
    fontWeight: 700,
    color: '#2d3436',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  sub: { fontSize: 11, color: '#95a5a6', fontWeight: 400, marginLeft: 'auto' },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  item: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0',
    borderBottom: '1px solid #f1f2f6',
    fontSize: 12,
  },
  code: { fontFamily: 'monospace', fontWeight: 600, color: '#2d3436' },
  rate: { color: '#636e72', fontSize: 11 },
  prior: { color: '#b2bec3' },
  growth: { fontWeight: 700, minWidth: 50, textAlign: 'right' },
  empty: { color: '#95a5a6', fontSize: 12, padding: '12px 0' },
};

function StatusBadge({ status, t1ParkingFlag }) {
  const key = (status || 'unknown').toLowerCase();
  let cfg = STATUS_STYLES[key] || {
    bg: '#ecf0f1', fg: '#636e72', label: (status || 'UNKNOWN').toUpperCase(),
  };
  // v1.10.45 — when the row is in T1 AND status is 'parking' (the §3.W
  // BAT_CHECK-flip case), show a red-outline variant instead of the filled
  // parking color, so the visual contrast makes "had a real error recently
  // but is parked now" clear next to "currently in error" (filled red).
  let extraStyle = {};
  if (t1ParkingFlag && key === 'parking') {
    cfg = { bg: '#fff', fg: '#d63031', label: 'PARKING' };
    extraStyle = { border: '2px solid #d63031', padding: '1px 6px' };
  }
  return (
    <span
      title={`Current status: ${status || 'unknown'}`}
      style={{
        display: 'inline-block',
        marginLeft: 6,
        padding: '2px 7px',
        borderRadius: 10,
        background: cfg.bg,
        color: cfg.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        ...extraStyle,
      }}
    >
      {cfg.label}
    </span>
  );
}

/**
 * Smart Analysis short text — 3 lines + freshness indicator + force-refresh.
 * Reads from robot.rca_short / robot.rca_freshness which is hydrated from
 * the smart_analysis_cache (or live updates that arrive via polling).
 */
// Inject spin keyframes once for the freshness indicator.
if (typeof document !== 'undefined' &&
    !document.getElementById('rca-spin-kf')) {
  const s = document.createElement('style');
  s.id = 'rca-spin-kf';
  s.textContent = '@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }';
  document.head.appendChild(s);
}

// v1.7 §13.C — category chip rendering.
// Each family has its own background colour (the v1.5 palette + v1.7 PCB tweak).
// Inside one chip: severity bullet (red/yellow) + family label + status
// word. Family + status share the chip's background and text colour;
// only the bullet uses a different colour to signal severity. Status
// word ("Issue?" / "FYI") is bold for emphasis.
//
// v1.7 changes:
//   - PCB chip background changed from gray (#cfd5dc) to pale mint
//     (#c4f0e0) with deep-forest fg (#0f6b46). Distinct from Flag's
//     bluer mint (#d9fbf5); not a "this is OK" signal — the red bullet
//     still makes "issue here" unambiguous.
//   - Status word for `failed` changed from "Active" to "Issue?". The
//     prior word read like "comms are working" on a Comm chip — the
//     opposite of the intended meaning. "Issue?" signals "possible
//     problem in this category, look here" without needing further
//     parsing of the bullet colour.
const CHIP_PALETTE = {
  battery:  { bg: '#e0e7ff', fg: '#4f46e5' },
  charging: { bg: '#fff3cd', fg: '#f39c12' },
  stuck:    { bg: '#ffe7c2', fg: '#d35400' },
  flag:     { bg: '#d9fbf5', fg: '#00a39c' },
  drive:    { bg: '#fef9c3', fg: '#ca8a04' },
  clean:    { bg: '#ffd8ea', fg: '#e84393' },
  comm:     { bg: '#eef0f2', fg: '#7f8c8d' },
  temp:     { bg: '#ffdec2', fg: '#e67e22' },
  pcb:      { bg: '#c4f0e0', fg: '#0f6b46' },  // v1.7: pale mint + forest
  fw:       { bg: '#e0e4e8', fg: '#636e72' },
  obstacle: { bg: '#cffafe', fg: '#0e7490' },
};
const CHIP_BULLET = {
  failed: '#d63031',  // red — active issue (numbered verdict)
  fyi:    '#fbc531',  // yellow — FYI (soft signal / probably fixed)
  watch:  '#fbc531',  // yellow — v1.10.3 Watch (Amendment I, observe & report)
  ok:     '#00b894',  // green — reserved (ok chips not rendered today)
};
// v1.10.3 — Watch state shows `?` instead of FYI tag (per spec: chip
// reads "Comm ?" not "Comm FYI" when state == 'watch').
const CHIP_TAG = { failed: 'Issue?', fyi: 'FYI', watch: '?', ok: 'OK' };

function CategoryChips({ categories }) {
  const visible = (categories || []).filter(
    c => c && (c.state === 'failed' || c.state === 'fyi' || c.state === 'watch')
  );
  if (visible.length === 0) return null;
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 6,
      marginBottom: 4,
    }}>
      {visible.map((c, i) => {
        const pal = CHIP_PALETTE[c.key] || { bg: '#e8eef3', fg: '#2d3436' };
        const dot = CHIP_BULLET[c.state] || '#95a5a6';
        const tag = CHIP_TAG[c.state] || '';
        return (
          <span
            key={`${c.key}-${i}`}
            title={
              c.state === 'failed' ? 'Active issue' :
              c.state === 'watch'  ? 'Watch / verify — observe and report' :
              'FYI / soft signal'
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: pal.bg,
              color: pal.fg,
              padding: '3px 9px',
              borderRadius: 4,
              fontSize: 12,
              lineHeight: 1.2,
            }}
          >
            <span aria-hidden="true" style={{ color: dot, fontSize: 13, lineHeight: 1 }}>●</span>
            {c.label}
            <span style={{ fontWeight: 800 }}>{tag}</span>
          </span>
        );
      })}
    </div>
  );
}

function RcaCell({ robot, onForceRefresh }) {
  const freshness = robot.rca_freshness || 'stale';
  const text = robot.rca_short || '';
  const categories = robot.rca_categories || [];

  // v1.5.3 diagnostic log — remove once chip rendering is confirmed.
  if (typeof window !== 'undefined' && !window.__fa_v153_logged) {
    window.__fa_v153_logged = true;
    // eslint-disable-next-line no-console
    console.log('[FaultAssignment v1.5.3] sample RcaCell robot=' + robot.robot_number,
      { categories, freshness, hasChipsCode: typeof CategoryChips });
  }

  // Freshness indicator
  let icon, iconColor, iconTitle, animate = false;
  if (freshness === 'fresh') {
    icon = '✓'; iconColor = '#00b894'; iconTitle = 'Smart Analysis up to date';
  } else if (freshness === 'refreshing') {
    icon = '⧗'; iconColor = '#0984e3'; animate = true;
    iconTitle = 'Refreshing Smart Analysis…';
  } else if (freshness === 'failed') {
    icon = '⚠'; iconColor = '#e17055'; iconTitle = 'Smart Analysis last attempt failed';
  } else {
    icon = '⧗'; iconColor = '#95a5a6'; iconTitle = 'Awaiting Smart Analysis';
  }

  if (!text) {
    return (
      <div style={styles.rcaCellWrap}>
        <div style={styles.rcaPlaceholder}>
          <span style={{ ...styles.freshDot, color: iconColor,
                         animation: animate ? 'spin 1.4s linear infinite' : undefined }}
                title={iconTitle}>{icon}</span>
          {freshness === 'refreshing' ? 'Analyzing…' : 'Open Smart Analysis →'}
        </div>
      </div>
    );
  }

  // v1.5 brief: 3 textual layers (mode-flag, verdict, bring). Layer 3
  // (category chips) is structured — rendered from `robot.rca_categories`,
  // injected just before the Bring line.
  // Mode flag styling per spec §13:
  //   🚨 inactiveAirtouch → red bold
  //   ⚠️ inactiveClient   → amber bold
  //   ⚪ inactive (generic) → gray bold
  //   🟣 portable          → purple bold
  // The legacy "Status: …" tag-line (v1.0–v1.4) is suppressed if it
  // appears in a stale cache row (defensive — backend stops emitting it
  // in v1.5).
  const allLines = text.split('\n');
  // Drop legacy Status: line if present (v1.4 → v1.5 transition).
  // v1.5.2: when the robot is inactive, the inactivePrefix div above the
  // RcaCell already shows "Inactive (Client) — since last N days" in the
  // mode-specific colour — drop the brief's Layer 1 mode-flag line so the
  // user doesn't see two lines of the same info.
  let lines = allLines.filter(l => !l.trim().startsWith('Status:'));
  if (robot.is_inactive) {
    lines = lines.filter(l => !/^[🚨⚠️⚪🟣]/.test(l.trim()));
    // Trim leading blank lines that may remain after filtering Layer 1.
    while (lines.length && lines[0].trim() === '') lines.shift();
  }
  const renderLine = (line, idx) => {
    const trimmed = line.trim();
    if (trimmed === '') return <div key={idx} style={{ height: 4 }} />;
    // v1.10.2 Phase A — freshness prefix line. Backend emits "✅ Ran OK ..."
    // or "⚠️ No clean cycle ...". Render as a colored bullet + gray text;
    // strip the emoji from the displayed text. Detected BEFORE the mode-flag
    // check so the ⚠️ doesn't accidentally get amber-mode-flag styling.
    const freshnessMatch = trimmed.match(/^(✅|⚠️)\s+(.+)$/);
    if (freshnessMatch && /Ran OK|clean cycle/.test(trimmed)) {
      const [, emoji, rest] = freshnessMatch;
      // v1.10.2 — Glossy 3D orb (matches the user's reference image: green
      // for OK, orange for warning). Built with a radial-gradient (highlight
      // upper-left, mid-tone center, dark edge) + soft drop shadow.
      const isGreen = emoji === '✅';
      const orb = isGreen
        ? { hi: '#c2f5cd', mid: '#22c55e', edge: '#0d5e26' }   // green orb
        : { hi: '#fff0c0', mid: '#ffb800', edge: '#aa7a00' };  // amber-yellow orb (less red)
      return (
        <div key={idx} style={{
          color: '#7f8c8d',           // muted gray text
          fontWeight: 400,            // not bold — orb does the work
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 3,
          marginTop: 1,
        }}>
          <span style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 30%, ${orb.hi} 0%, ${orb.mid} 55%, ${orb.edge} 100%)`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.25), inset 0 -1px 1px rgba(0,0,0,0.15)',
            flexShrink: 0,
          }} aria-hidden="true" />
          {rest}
        </div>
      );
    }
    if (/^[🚨⚠️⚪🟣]/.test(trimmed)) {
      let modeColor = '#636e72';  // generic inactive default
      if (trimmed.startsWith('🚨')) modeColor = '#d63031';        // inactiveAirtouch — red
      else if (trimmed.startsWith('⚠️')) modeColor = '#d35400';   // inactiveClient — amber
      else if (trimmed.startsWith('🟣')) modeColor = '#6c5ce7';   // portable — purple
      return (
        <div key={idx} style={{ ...styles.rcaModeFlag, color: modeColor }}>
          {trimmed}
        </div>
      );
    }
    if (/^\d+\)/.test(trimmed)) {
      return <div key={idx} style={styles.rcaVerdict}>{trimmed}</div>;
    }
    if (trimmed.startsWith('Bring:')) {
      return <div key={idx} style={styles.rcaBring}>{trimmed}</div>;
    }
    return <div key={idx} style={styles.rcaBody}>{trimmed}</div>;
  };

  // Render: first non-blank line gets the freshness icon prefix —
  // EXCEPT the v1.10.2 freshness prefix (✅/⚠️ "Ran OK..." / "No clean cycle...")
  // which is its own visual signal. The freshness ✓ icon attaches to the
  // first line AFTER the prefix (typically the verdict).
  // Inject the chip row right before the first Bring: line so chips
  // sit visually between the numbered verdict (Layer 2) and Bring (L4).
  const isFreshnessPrefix = (l) => {
    const t = l.trim();
    return /^(✅|⚠️)\s/.test(t) && /Ran OK|clean cycle/.test(t);
  };
  let iconPlaced = false;
  let chipsRendered = false;
  const rendered = [];
  lines.forEach((line, idx) => {
    if (!chipsRendered && line.trim().startsWith('Bring:')) {
      rendered.push(<CategoryChips key="chips" categories={categories} />);
      chipsRendered = true;
    }
    if (!iconPlaced && line.trim() !== '' && !isFreshnessPrefix(line)) {
      iconPlaced = true;
      const inner = renderLine(line, idx);
      rendered.push(
        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <span style={{ ...styles.freshDot, color: iconColor,
                         animation: animate ? 'spin 1.4s linear infinite' : undefined }}
                title={iconTitle}>{icon}</span>
          {inner}
        </div>
      );
    } else {
      rendered.push(renderLine(line, idx));
    }
  });
  // If there was no Bring: line (defensive), still show chips at the end.
  if (!chipsRendered) {
    rendered.push(<CategoryChips key="chips" categories={categories} />);
  }

  return (
    <div style={styles.rcaCellWrap}>
      {rendered}
      {freshness !== 'refreshing' && onForceRefresh && (
        <button
          onClick={onForceRefresh}
          style={styles.rcaRefreshBtn}
          title="Re-run Smart Analysis for this robot"
        >
          ↻ refresh
        </button>
      )}
    </div>
  );
}

/**
 * Tooltip component — shows on hover with robot details
 */
function RobotTooltip({ robot, visible, x, y }) {
  if (!visible || !robot) return null;

  const sevLabel = robot.top_severity === 'red' ? 'CRITICAL' : robot.top_severity === 'yellow' ? 'WARNING' : 'LOW';
  const sevColor = robot.top_severity === 'red' ? '#ff7675' : robot.top_severity === 'yellow' ? '#ffeaa7' : '#55efc4';

  return (
    <div style={{
      position: 'fixed',
      left: Math.min(x + 12, window.innerWidth - 380),
      top: Math.min(y + 12, window.innerHeight - 350),
      width: 360,
      background: '#1a1a2e',
      color: '#fff',
      borderRadius: 10,
      padding: 16,
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      zIndex: 9999,
      fontSize: 12,
      lineHeight: 1.5,
      pointerEvents: 'none',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#a29bfe' }}>
          Robot #{robot.robot_number}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: sevColor, padding: '2px 8px', background: 'rgba(255,255,255,0.1)', borderRadius: 4 }}>
          {sevLabel}
        </span>
      </div>

      {/* Main error */}
      <div style={{ marginBottom: 10 }}>
        <span style={{ color: '#b2bec3', fontSize: 11 }}>Main Error: </span>
        <span style={{ fontFamily: 'monospace', color: '#ffeaa7', fontWeight: 600 }}>{humanizeCode(robot.top_error) || 'N/A'}</span>
      </div>

      {/* Stats row */}
      <div style={{ marginBottom: 10, display: 'flex', gap: 16 }}>
        <div>
          <span style={{ color: '#b2bec3' }}>Faults: </span>
          <span style={{ fontWeight: 600 }}>{robot.total_faults || 0}</span>
          {robot.important_faults !== robot.total_faults && (
            <span style={{ color: '#b2bec3', fontSize: 10 }}> ({robot.important_faults} important)</span>
          )}
        </div>
        <div>
          <span style={{ color: '#b2bec3' }}>Types: </span>
          <span>{robot.unique_errors || '?'}</span>
        </div>
        {robot.risk_score > 0 && (
          <div>
            <span style={{ color: '#b2bec3' }}>Risk: </span>
            <span style={{ fontWeight: 600, color: robot.risk_score >= 60 ? '#ff7675' : '#ffeaa7' }}>
              {robot.risk_score}
            </span>
          </div>
        )}
      </div>

      {/* Full error history */}
      {robot.error_history?.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ color: '#b2bec3', fontSize: 11, marginBottom: 4, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 3, fontWeight: 600 }}>
            Error History:
          </div>
          {robot.error_history.slice(0, 8).map((e, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
              // Less aggressive dim on non-important rows so the text stays
              // legible on the dark background.
              opacity: e.is_important ? 1 : 0.75,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: e.severity === 'red' ? '#ff7675' : e.severity === 'yellow' ? '#ffeaa7' : '#55efc4',
              }}/>
              <span style={{ fontSize: 11, color: '#f5f6fa', flex: 1 }}>
                {errorWithLocation(e.code, e.row, e.location_on_row)}
              </span>
              {e.time && (
                <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                  background: 'rgba(255,255,255,0.12)',
                  padding: '2px 8px',
                  borderRadius: 10,
                  whiteSpace: 'nowrap',
                  letterSpacing: 0.2,
                }}>
                  {timeAgo(e.time)} ago
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Factors */}
      {robot.factors?.length > 0 && (
        <div>
          <div style={{ color: '#999', fontSize: 11, marginBottom: 3 }}>Analysis:</div>
          {robot.factors.map((f, i) => (
            <div key={i} style={{ paddingLeft: 8, color: '#b2bec3', fontSize: 11 }}>• {f}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Fault Assignment page — Manager assigns robots to technicians.
 *
 * Layout:
 * - Project selector: Country >> Region >> Projects (with Recent section)
 * - KPI summary cards
 * - Robot table: number, fault type, summary, equipment, technician assignment
 * - 4 action buttons per robot: Check Status, Reset, Go Home, Activate
 */

// --- Region mapping (same as ProjectList) ---
const REGION_MAP = {
  Israel: {
    'נגב מערבי': ['Lotan', 'Samar', 'Neot Smadar', 'Mitzpe Ramon', 'Sde Boker', 'revivim',
      'mashabey sade', 'Beit Kama', 'Gvulot', 'Givolim', 'Yakhini', 'Ein Habsor',
      'Nachal Oz', 'Nirim', 'Nir Itzhak', 'Sufa', 'Kfar Aza', 'Pri Gan', 'Tarabin', 'Cramim',
      'Lahav', 'Arad', 'Ein Tzurim', 'bikura'],
    'צפון': ['Kfar Hanassi', 'Lavi', 'Sde Nachum', 'Kfar Blum', 'Mahanayim', 'Kidmat tzvi'],
    'מרכז': ['Rehovot'],
  },
  India: {
    'Rajasthan': ['Pokrhan', 'Pokhran', 'Bhadla', 'Bikaner', 'Rajasthan', 'Nokhara',
      'Dedasari', 'Nimba', 'Toga', '.'],
    'Gujarat': ['Gujarat', 'Modha', 'Hasapur'],
    'Maharashtra': ['Maharashtra', 'Pangaon', 'Dhamangaon'],
    'Karnataka': ['Koppal', 'Kudligi', 'Arni'],
  },
};

function getRegion(country, city) {
  const countryMap = REGION_MAP[country];
  if (countryMap) {
    for (const [region, cities] of Object.entries(countryMap)) {
      if (cities.includes(city)) return region;
    }
  }
  return 'Other';
}

const RECENT_KEY = 'airtouch_mgr_recent_projects';

function getRecentProjects() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function saveRecentProject(id, name) {
  const recent = getRecentProjects().filter(r => r.id !== id);
  recent.unshift({ id, name, ts: Date.now() });
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 5)));
}

// --- Severity colors ---
const SEV_COLORS = {
  critical: { bg: '#ffe0e0', text: '#c0392b' },
  high: { bg: '#fff3cd', text: '#d68910' },
  normal: { bg: '#e8f5e9', text: '#27ae60' },
};

export default function FaultAssignment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
  const [sortMode, setSortMode] = useState('status_first');  // 'status_first' | 'number_only'
  // v1.10.4 (2026-05-06) — Assignment-list filter. Default = filtered (only
  // robots with ≤1 clean cycle since last REAL error). Toggle to "show all"
  // displays every disabled robot. Backend honors `show_all=true` query param.
  const [showAll, setShowAll] = useState(false);

  const [assignments, setAssignments] = useState({}); // robotNumber -> techName
  const [managerNotes, setManagerNotes] = useState({}); // robotNumber -> note text
  const [actionFeedback, setActionFeedback] = useState({}); // robotNumber -> message
  const [selectorOpen, setSelectorOpen] = useState(!searchParams.get('project'));
  const [tooltip, setTooltip] = useState({ visible: false, robot: null, x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState('error'); // 'error' | 'assigned' | 'closed'

  const { data: projectsData, loading: loadingProjects } = useAPI(getProjects);
  const { data: techData } = useAPI(() => getTechnicianKPIs(90));
  const { data: techMgmtData } = useAPI(getTechnicians);
  // v1.10.11 — pull v1.10.9 unified user list and merge into the dropdown
  // source so techId (e.g. "tech-il-001") is the canonical ID written when
  // a manager assigns. Falls back to legacy techMgmtData when /api/users
  // returns nothing (ensures continuity with pre-v1.10.9 environments).
  const { data: usersV2Data } = useAPI(() => getUsers({ role: 'tech' }));
  // v1.10.2 Change 3 — when selectedProject === ALL_ISRAEL, fanout-fetch
  // every Israel project's analysis in parallel, merge robots tagging each
  // with its source project. Triggers a per-project section header in the
  // robot table further down.
  const israelProjectIds = useMemo(() => {
    const projectsList = projectsData?.projects || [];
    return projectsList
      .filter(p => (p.country === 'Israel' || p.country_code === 'IL'))
      .map(p => p.id);
  }, [projectsData]);
  const israelProjectName = useCallback((pid) => {
    const projectsList = projectsData?.projects || [];
    return (projectsList.find(p => p.id === pid) || {}).name || '';
  }, [projectsData]);
  const { data: analysisData, loading: loadingAnalysis } = useAPI(
    () => {
      if (!selectedProject) return Promise.resolve(null);
      if (selectedProject === ALL_ISRAEL) {
        if (!israelProjectIds.length) return Promise.resolve({ disabled_robots: [] });
        return Promise.all(
          israelProjectIds.map(pid =>
            analyzeProject(pid, { days: 30, showAll })
              .then(r => ({ pid, r }))
              .catch(() => ({ pid, r: { disabled_robots: [] } }))
          )
        ).then(results => {
          const merged = [];
          let kept = 0, total = 0;
          for (const { pid, r } of results) {
            const pname = israelProjectName(pid);
            for (const robot of (r?.disabled_robots || [])) {
              merged.push({ ...robot, _source_project_id: pid, _source_project_name: pname });
            }
            const af = r?.assign_filter;
            if (af) { kept += af.kept || 0; total += af.total || 0; }
          }
          return {
            disabled_robots: merged,
            _aggregated: 'all_israel',
            assign_filter: { show_all: showAll, kept, total },
          };
        });
      }
      return analyzeProject(selectedProject, { days: 30, showAll });
    },
    [selectedProject, israelProjectIds.join(','), israelProjectName, showAll]
  );
  // Weekly trends cache (shared with Errors Trend page) — used to populate
  // the per-project Rising / Improving summary boxes on top of the page.
  const { data: trendsData } = useAPI(getWeeklyTrends);

  const projects = projectsData?.projects || [];
  const technicians = techData?.rankings || [];
  // Use managed technicians (with IDs) for assignment, fallback to KPI list
  // v1.10.11 — prefer v1.10.9 unified users (keys on techId), fall back
  // to legacy technicians-mgmt source for environments still on Mongo IDs.
  const allTechnicians = useMemo(() => {
    const v2list = usersV2Data?.users || [];
    if (v2list.length) {
      return v2list.map(u => ({
        id: u.techId || u.id,
        fullName: u.full_name || u.name || u.techId,
        role: 'technician',
        plantName: (u.sites && u.sites[0]) || u.homeSite || '',
        projects: u.projects || [],
        sites: u.sites || [],
      }));
    }
    return techMgmtData?.technicians || [];
  }, [usersV2Data, techMgmtData]);
  // Robots currently on error — only show those with a real actionable top error.
  // - Must be red (critical) or yellow (high) severity
  // - top_error must NOT be NO_EP_REPLY or flag-sensor codes. These are too noisy
  //   to warrant a field visit on their own. A robot with a red/yellow error that
  //   also happens to carry NO_EP_REPLY or flag errors still appears, because
  //   top_error is computed from the "important" codes when any exist.
  // - Filter out robots that have already been assigned.
  const NO_SOLO_ERRORS = new Set(['NO_EP_REPLY', 'HOME_FLAG_PROBLEM', 'FAR_END_FLAG_PROBLEM']);
  const erroredRobotsRaw = (analysisData?.disabled_robots || []).filter(
    r => !assignments[r.robot_number]
      && (r.top_severity === 'red' || r.top_severity === 'yellow' || r.is_inactive)
      && !NO_SOLO_ERRORS.has(r.top_error)
  );

  // Live overrides for Smart-Analysis short text. Keyed by robot_number,
  // updated as the polling endpoint returns refreshed cache rows.
  const [rcaLive, setRcaLive] = useState({});

  // Reset live overrides whenever a new project is selected / data reloads.
  useEffect(() => { setRcaLive({}); }, [selectedProject]);

  // Merge: live overrides win over the initial analyzeProject() snapshot.
  // v1.8 sort: two groups — currently-on-error first, "before" group second.
  // Within each group, sort by robot_number ascending (small → big) so the
  // tech can find a specific robot quickly.
  const erroredRobots = erroredRobotsRaw
    .map(r => {
      const live = rcaLive[r.robot_number];
      if (!live) return r;
      return { ...r, ...live };
    })
    .sort((a, b) => {
      // v1.10.2 Change 3 — ALL_ISRAEL view groups by source project first,
      // then robot_number within project. The status-first / number-only
      // sortMode applies only in single-project view.
      if (selectedProject === ALL_ISRAEL) {
        const pn = (a._source_project_name || '').localeCompare(b._source_project_name || '');
        if (pn !== 0) return pn;
        return (a.robot_number || 0) - (b.robot_number || 0);
      }
      // v1.10.45 — 'status_first' (default) sorts into 3 tiers:
      //   T1: real error in LAST cycle (last_cycle_kind === 'real')
      //       OR currently_on_error AND a real error within last 24h
      //         (Change E: stale-Mongo-flag + NEP-only filter)
      //   T2: obstacle-only error in last cycle (kind === 'obstacle_only')
      //       OR real error 2 cycles ago (last_error_cycle_index === 2)
      //   T3: older / chronic / null
      // Within T1: sort by severity (red, yellow, other), then ascending robot_number.
      // Within T2 / T3: ascending robot_number.
      // 'number_only' bypasses tiers and sorts purely by robot_number.
      if (sortMode === 'status_first') {
        const tier = (r) => {
          // v1.10.45b — only the LAST CYCLE classification matters for T1.
          // The 24h-real-error fallback was dropped 2026-06-03 per Yanir:
          // a robot whose last cleaning cycle ran clean is operationally
          // recovered, regardless of Mongo's currentStatus flag.
          const k = r.last_cycle_kind;
          if (k === 'real') return 1;
          if (k === 'obstacle_only') return 2;
          if (r.last_error_cycle_index === 2) return 2;
          return 3;
        };
        const ta = tier(a);
        const tb = tier(b);
        if (ta !== tb) return ta - tb;
        // T1 internal sort: severity rank (red=0, yellow=1, else=2), then number.
        if (ta === 1) {
          const sevRank = (r) => r.top_severity === 'red' ? 0 : r.top_severity === 'yellow' ? 1 : 2;
          const sr = sevRank(a) - sevRank(b);
          if (sr !== 0) return sr;
        }
      }
      return (a.robot_number || 0) - (b.robot_number || 0);
    });

  // Polling: while any visible row is in 'refreshing' state, hit the
  // status endpoint every 8s. Stop when all rows are fresh.
  useEffect(() => {
    if (!selectedProject) return undefined;
    const stale = erroredRobots
      .filter(r => r.kind !== 'repeater' && r.rca_freshness === 'refreshing')
      .map(r => r.robot_number);
    if (stale.length === 0) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const r = await getRcaRefreshStatus(selectedProject, stale);
        if (cancelled) return;
        const updates = {};
        for (const u of (r?.updates || [])) {
          updates[u.robot_number] = {
            rca_short: u.rca_short,
            rca_freshness: u.rca_freshness,
            rca_generated_at: u.rca_generated_at,
            // v1.6.1: propagate chip data so the chip row updates in-place
            // when a refresh completes mid-page-lifetime. Backend now
            // returns rca_categories on the rca-refresh-status endpoint.
            rca_categories: u.rca_categories,
          };
        }
        setRcaLive(prev => ({ ...prev, ...updates }));
      } catch (e) {
        console.error('rca poll failed', e);
      }
    };
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line
  }, [selectedProject, erroredRobots.map(r => `${r.robot_number}:${r.rca_freshness}`).join('|')]);

  // Manual force-refresh: re-queue and immediately switch the row to
  // 'refreshing' so the user sees feedback.
  const handleForceRcaRefresh = useCallback(async (robotNumber) => {
    if (!selectedProject) return;
    setRcaLive(prev => ({
      ...prev,
      [robotNumber]: {
        rca_short: prev[robotNumber]?.rca_short || '',
        rca_freshness: 'refreshing',
        rca_generated_at: prev[robotNumber]?.rca_generated_at,
        // v1.6.1: keep prior chips visible during the refresh — without
        // this, the chip row blanks out the moment the user clicks the
        // refresh button and only reappears after the poll completes.
        rca_categories: prev[robotNumber]?.rca_categories,
      },
    }));
    try { await forceRcaRefresh(selectedProject, robotNumber); }
    catch (e) { console.error('force refresh failed', e); }
  }, [selectedProject]);

  // Filter technicians to those whose Technician-page record explicitly lists
  // the selected project in their `projects` array. Same rule for Israel and
  // India — keeps the dropdown short and matches what's editable on the
  // Technician Management page.
  // Always hide test/demo accounts and known non-field admins.
  const projectTechnicians = useMemo(() => {
    if (!selectedProject || !allTechnicians.length) return [];
    const EXCLUDE_BY_NAME = new Set(['Nadav Arnon', 'Ori Weiss']);
    return allTechnicians.filter(t => {
      // v1.10.11 — accept tech if either (a) projects[] includes selected,
      // OR (b) projects[] is empty (v1.10.9 shape may omit it). Region-
      // restriction is enforced server-side on /api/users.
      const projs = t.projects || [];
      if (projs.length > 0 && !projs.includes(selectedProject)) return false;
      // Strip noise
      const name = (t.fullName || '').toLowerCase();
      if (/\b(test|demo|example)\b/.test(name)) return false;
      if (EXCLUDE_BY_NAME.has(t.fullName)) return false;
      return true;
    });
  }, [selectedProject, allTechnicians]);

  // v1.10.11 — Persistent assignment poller. Keeps the local assignments
  // map in sync with /api/assignments so when a tech bounces a robot via
  // mobile (PATCH .../return → status='returned'), the desktop drops it
  // from "Assigned" and reflows it back to the main list with a "Fail to
  // fix" chip. Polls every 30s plus on-demand after manager-assign.
  const [returnedRobots, setReturnedRobots] = useState({}); // robot_number -> {reason_code, reason_text, returned_at}
  const reconcileFromBackend = useCallback(async () => {
    if (!selectedProject || selectedProject === ALL_ISRAEL) return;
    try {
      const rows = await listAssignments({ project_id: selectedProject, limit: 200 });
      if (!Array.isArray(rows)) return;
      // Most-recent first → for each robot keep the freshest row only.
      const seen = new Set();
      const latest = [];
      for (const r of rows) {
        const rn = r.robot_number;
        if (rn == null || seen.has(rn)) continue;
        seen.add(rn);
        latest.push(r);
      }
      const nextAssign = {};
      const nextReturned = {};
      for (const r of latest) {
        const rn = r.robot_number;
        const st = (r.status || '').toLowerCase();
        if (st === 'open' || st === 'in_progress' || st === 'pulled' || st === 'pending_review') {
          nextAssign[rn] = r.technician_name || r.technician_id || '';
        } else if (st === 'returned') {
          nextReturned[rn] = {
            reason_code: r.returned_reason_code || null,
            reason_text: r.returned_reason || null,
            returned_at: r.returned_at || r.updated_at,
          };
        }
      }
      setAssignments(prev => ({ ...nextAssign, ...prev })); // optimistic-local wins
      setReturnedRobots(nextReturned);
    } catch (_e) { /* swallow — keep existing state */ }
  }, [selectedProject]);
  useEffect(() => {
    reconcileFromBackend();
    const t = setInterval(reconcileFromBackend, 30000);
    return () => clearInterval(t);
  }, [reconcileFromBackend]);

  // When project is selected from URL, save to recent
  useEffect(() => {
    if (selectedProject && projects.length > 0) {
      const p = projects.find(p => p.id === selectedProject);
      if (p) saveRecentProject(p.id, p.name);
    }
  }, [selectedProject, projects]);

  // Build grouped project selector data
  const groupedProjects = useMemo(() => {
    if (!projects.length) return {};
    const result = {};
    for (const p of projects) {
      const country = p.country || 'Other';
      const region = getRegion(country, p.city);
      if (!result[country]) result[country] = {};
      if (!result[country][region]) result[country][region] = [];
      result[country][region].push(p);
    }
    // Sort within regions alphabetically
    for (const country of Object.keys(result)) {
      for (const region of Object.keys(result[country])) {
        result[country][region].sort((a, b) => a.name.localeCompare(b.name));
      }
    }
    return result;
  }, [projects]);

  const selectedProjectName = useMemo(
    () => {
      if (selectedProject === ALL_ISRAEL) return 'All Israel';
      return projects.find(p => p.id === selectedProject)?.name || '';
    },
    [projects, selectedProject]
  );

  const handleSelectProject = useCallback((id) => {
    setSelectedProject(id);
    setSelectorOpen(false);
    const p = projects.find(p => p.id === id);
    if (p) saveRecentProject(p.id, p.name);
  }, [projects]);

  const handleAction = useCallback((robotNumber, action) => {
    setActionFeedback(prev => ({ ...prev, [robotNumber]: `${action}...` }));
    // Simulate API call
    setTimeout(() => {
      setActionFeedback(prev => ({
        ...prev,
        [robotNumber]: `${action} sent`,
      }));
      setTimeout(() => {
        setActionFeedback(prev => {
          const copy = { ...prev };
          delete copy[robotNumber];
          return copy;
        });
      }, 2000);
    }, 800);
  }, []);

  // ── v1.10.53 Robot control buttons (Start / Go Home / Reset / Check Battery) ──
  // Per-robot UI state: rc[robot_number] = { sending, locked, countdown, lastEvent,
  //   line, lineTs, failed, panelOpen, batt:{loading,reading,history,offline,empty,error} }
  const [rc, setRc] = useState({});
  const rcTimers = useRef({});
  const rcSet = useCallback((rn, patch) => {
    setRc(prev => ({ ...prev, [rn]: { ...(prev[rn] || {}), ...patch } }));
  }, []);
  const rcPidFor = useCallback((robot) => robot._source_project_id || selectedProject, [selectedProject]);
  const RC_EVENT_LINE = {
    START_CLEANING: '🟢 Drive Forward',
    GO_HOME: '🏠 Going Home',
    RESET: '🔄 Reset sent — wait 30 sec to reactivate',
  };
  const rcNowStr = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const rcStartCountdown = useCallback((rn) => {
    clearInterval(rcTimers.current[rn]);
    rcSet(rn, { locked: true, countdown: 30 });
    rcTimers.current[rn] = setInterval(() => {
      setRc(prev => {
        const cur = prev[rn] || {};
        const c = (cur.countdown || 0) - 1;
        if (c <= 0) { clearInterval(rcTimers.current[rn]); return { ...prev, [rn]: { ...cur, locked: false, countdown: 0 } }; }
        return { ...prev, [rn]: { ...cur, countdown: c } };
      });
    }, 1000);
  }, [rcSet]);
  const rcWithTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('ack-timeout')), ms))]);
  const rcDoCommand = useCallback(async (robot, event) => {
    const rn = robot.robot_number; const robotId = robot.robot_id; const pid = rcPidFor(robot);
    if (!robotId) { rcSet(rn, { panelOpen: true, failed: true, line: 'Missing robot id — reload the page', lineTs: rcNowStr() }); return; }
    rcSet(rn, { sending: event, panelOpen: true, failed: false, line: null });
    try {
      const res = await rcWithTimeout(robotCommand(robotId, pid, event), 3000);
      if (res && res.ok) {
        rcSet(rn, { sending: null, lastEvent: event, failed: false,
                    line: `${RC_EVENT_LINE[event]} · ${rcNowStr()}`, lineTs: rcNowStr() });
        rcStartCountdown(rn);
      } else {
        rcSet(rn, { sending: null, failed: true, line: 'Failed — Try Again', lineTs: rcNowStr() });
      }
    } catch (e) {
      rcSet(rn, { sending: null, failed: true, line: 'Failed — Try Again', lineTs: rcNowStr() });
    }
  }, [rcSet, rcPidFor, rcStartCountdown]);
  const rcSleep = (ms) => new Promise(r => setTimeout(r, ms));
  const rcDoBattery = useCallback(async (robot) => {
    const rn = robot.robot_number; const robotId = robot.robot_id; const pid = rcPidFor(robot);
    if (!robotId) { rcSet(rn, { panelOpen: true, batt: { error: true, message: 'Missing robot id — reload' } }); return; }
    rcSet(rn, { sending: 'BATTERY', panelOpen: true, failed: false, batt: { loading: true } });
    try {
      const ref = await robotBatteryRefresh(robotId, pid);
      if (!ref || !ref.ok) {
        rcSet(rn, { sending: null, batt: { offline: !!(ref && ref.offline), error: !(ref && ref.offline), message: (ref && ref.message) || 'could not start reading' } });
        return;
      }
      rcStartCountdown(rn);
      let reading = null;
      for (let i = 0; i < 12 && !reading; i++) {
        await rcSleep(2500);
        try { const lr = await robotBatteryLiveReport(pid, ref.processId); if (lr && lr.ready && lr.reading) reading = lr.reading; } catch (e) {}
      }
      if (reading) {
        const ts = new Date().toISOString();
        try { await robotBatteryStore({ robotId, projectId: pid, robotNumber: rn, voltage: reading.voltage, soc: reading.soc, charging_current_a: reading.charging_current_a, ts }); } catch (e) {}
        let history = [];
        try { const h = await robotBatteryHistory(robotId, 4, pid, rn); history = (h && h.readings) || []; } catch (e) {}
        rcSet(rn, { sending: null, batt: { loading: false, reading, history } });
      } else {
        rcSet(rn, { sending: null, batt: { loading: false, empty: true } });
      }
    } catch (e) {
      rcSet(rn, { sending: null, batt: { loading: false, error: true } });
    }
  }, [rcSet, rcPidFor, rcStartCountdown]);

  if (loadingProjects) return <div style={{ padding: 40, color: '#999' }}>Loading...</div>;

  const recentProjects = getRecentProjects();

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Fault Assignment</h1>
          <p style={styles.subtitle}>Assign repair tasks to technicians</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {selectedProject && activeTab === 'error' && (
            <button
              style={styles.changeProjBtn}
              onClick={() => setSelectorOpen(!selectorOpen)}
            >
              <SiteAlertBullet projectId={selectedProject} />
              {selectedProjectName} ▾
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'error' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('error')}
        >
          Currently on Error
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'tracking' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('tracking')}
        >
          Tracking
        </button>
      </div>

      {/* v1.10.18 — Tracking: 3-zone assignment lifecycle */}
      {activeTab === 'tracking' && (
        <TrackingView
          selectedProject={selectedProject}
          allIsrael={selectedProject === ALL_ISRAEL}
          israelProjectIds={israelProjectIds}
        />
      )}

      {/* Currently on Error tab content */}
      {activeTab === 'error' && <>

      {/* Project Selector */}
      {selectorOpen && (
        <div style={styles.selectorPanel}>
          {/* Recent projects */}
          {recentProjects.length > 0 && (
            <div style={styles.selectorGroup}>
              <div style={styles.selectorGroupTitle}>Recent</div>
              <div style={styles.recentRow}>
                {recentProjects.map(r => (
                  <button
                    key={r.id}
                    style={{
                      ...styles.recentBtn,
                      ...(r.id === selectedProject ? styles.recentBtnActive : {}),
                    }}
                    onClick={() => handleSelectProject(r.id)}
                  >
                    <SiteAlertBullet projectId={r.id} />
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Country >> Region >> Projects */}
          {['Israel', 'India', 'United Arab Emirates'].map(country => {
            const regions = groupedProjects[country];
            if (!regions) return null;
            return (
              <div key={country} style={styles.selectorGroup}>
                <div style={styles.selectorCountry}>
                  {country === 'Israel' ? '🇮🇱' : country === 'India' ? '🇮🇳' : '🇦🇪'} {country}
                </div>
                {/* v1.10.2 Change 3 — All-Israel aggregator pseudo-project. */}
                {country === 'Israel' && (
                  <div style={styles.selectorRegion}>
                    <div style={styles.selectorProjects}>
                      <button
                        style={{
                          ...styles.selectorProjectBtn,
                          ...(selectedProject === ALL_ISRAEL ? styles.selectorProjectBtnActive : {}),
                          fontWeight: 700,
                        }}
                        onClick={() => handleSelectProject(ALL_ISRAEL)}
                      >
                        🇮🇱 All Israel (aggregated view)
                      </button>
                    </div>
                  </div>
                )}
                {Object.entries(regions).map(([region, projs]) => (
                  <div key={region} style={styles.selectorRegion}>
                    <div style={styles.selectorRegionLabel}>{region}</div>
                    <div style={styles.selectorProjects}>
                      {projs.map(p => (
                        <button
                          key={p.id}
                          style={{
                            ...styles.selectorProjectBtn,
                            ...(p.id === selectedProject ? styles.selectorProjectBtnActive : {}),
                          }}
                          onClick={() => handleSelectProject(p.id)}
                        >
                          <SiteAlertBullet projectId={p.id} />
                          {p.name}
                          <span style={styles.selectorRobotCount}>{p.total_robots}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Main content when project selected */}
      {!selectedProject ? (
        !selectorOpen && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>&#9881;</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Select a Project</div>
            <div style={{ fontSize: 13, color: '#999' }}>
              Choose a project to view faults and assign repair tasks
            </div>
            <button style={styles.openSelectorBtn} onClick={() => setSelectorOpen(true)}>
              Choose Project
            </button>
          </div>
        )
      ) : loadingAnalysis ? (
        <div style={{ padding: 40, color: '#999', textAlign: 'center' }}>Analyzing faults...</div>
      ) : (
        <>
          {/* Per-project trend boxes — source: weekly trends cache */}
          <ProjectTrendsRow
            projectName={selectedProjectName}
            trendsData={trendsData}
          />

          {/* Summary stat cards.
              v1.10.2 Change 2 — replaced 'Technicians' with aggregated
              spare-parts box (counts each part across currently-on-error
              robots' Bring lists, excluding 'tools' / 'tape measure'). */}
          <div style={styles.cardGridTwo}>
            <StatCard
              label="Currently on Error"
              value={erroredRobots.filter(r => r.currently_on_error).length}
              sub={`${erroredRobots.length} total to assign`}
              color="#e17055"
            />
            <SparePartsCard robots={erroredRobots} />
          </div>

          {/* v1.5.3 visible version marker (data-fa-version) + title. */}
          <h2 style={styles.sectionTitle} data-fa-version="v1.5.3-chips-bg">
            Robots with errors in the last 2 cycles
            {selectedProjectName ? ' in ' : ''}
            {selectedProjectName && (
              <span style={{
                fontSize: 22,
                fontWeight: 800,
                color: '#6c5ce7',
                fontFamily: 'Georgia, "Times New Roman", serif',
                letterSpacing: 0.3,
                marginLeft: 4,
                marginRight: 4,
              }}>{selectedProjectName}</span>
            )}
            {/* v1.10.2 Change 1 — sort-mode toggle (single-project view only). */}
            {selectedProject !== ALL_ISRAEL && (
              <span style={{ marginLeft: 12, display: 'inline-flex',
                              alignItems: 'center', gap: 4, fontSize: 12 }}>
                <span style={{ color: '#7f8c8d' }}>Sort:</span>
                <button
                  onClick={() => setSortMode('status_first')}
                  style={{
                    padding: '3px 9px', borderRadius: 4,
                    border: '1px solid ' + (sortMode === 'status_first' ? '#475569' : '#cbd5e1'),
                    background: sortMode === 'status_first' ? '#475569' : '#fff',
                    color: sortMode === 'status_first' ? '#fff' : '#475569',
                    cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                    fontWeight: 500,
                  }}
                >By cycle recency</button>
                <button
                  onClick={() => setSortMode('number_only')}
                  style={{
                    padding: '3px 9px', borderRadius: 4,
                    border: '1px solid ' + (sortMode === 'number_only' ? '#475569' : '#cbd5e1'),
                    background: sortMode === 'number_only' ? '#475569' : '#fff',
                    color: sortMode === 'number_only' ? '#fff' : '#475569',
                    cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                    fontWeight: 500,
                  }}
                >By number</button>
              </span>
            )}
            {/* v1.10.4 — Assignment-list filter toggle. Default = filtered
                (only robots with ≤1 clean cycle since their last REAL error
                — NEP and unpaired/sub-threshold flags don't count). Toggle
                on → "show all" passes show_all=true to the backend. */}
            <span style={{ marginLeft: 12, display: 'inline-flex',
                            alignItems: 'center', gap: 4, fontSize: 12 }}>
              <button
                onClick={() => setShowAll(!showAll)}
                title={showAll
                  ? 'Currently showing every disabled robot. Click to filter to robots actively in trouble.'
                  : 'Currently showing only robots with ≤1 clean cycle since their last real error (excluding NEP and lone flags). Click to show every disabled robot.'}
                style={{
                  padding: '3px 9px', borderRadius: 4,
                  border: '1px solid ' + (showAll ? '#0e7490' : '#cbd5e1'),
                  background: showAll ? '#0e7490' : '#fff',
                  color: showAll ? '#fff' : '#475569',
                  cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                  fontWeight: 500,
                }}
              >
                {showAll ? 'Showing all robots — click to filter' : 'Show all robots'}
              </button>
              {analysisData?.assign_filter && !showAll && (
                <span style={{ marginLeft: 6, color: '#7f8c8d', fontSize: 11 }}>
                  Showing {analysisData.assign_filter.kept} of {analysisData.assign_filter.total}
                  {' '}(filtered to actively-erroring)
                </span>
              )}
            </span>
            <span style={{
              fontSize: 9,
              color: '#bbb',
              marginLeft: 12,
              fontWeight: 400,
              letterSpacing: 0.5,
            }}>build v1.5.3</span>
          </h2>

          <div style={styles.tableWrapper}>
            <table style={styles.robotTable}>
              <thead>
                <tr style={styles.robotTh}>
                  <th style={styles.thCell}>Robot #</th>
                  <th style={styles.thCell}>Error</th>
                  <th style={{ ...styles.thCell, minWidth: 260 }}>Issue (RCA)</th>
                  <th style={styles.thCell}>Assign To</th>
                  <th style={{ ...styles.thCell, minWidth: 160 }}>Manager Note</th>
                  <th style={{ ...styles.thCell, minWidth: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {erroredRobots.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                      No robots currently on error in this project
                    </td>
                  </tr>
                )}
                {erroredRobots.map((robot, i, arr) => {  /* v1.10.54 — 200-row cap removed; show every qualifying robot (tiers prioritize) */
                  // v1.10.2 Change 3 — per-project section header in ALL_ISRAEL view.
                  const prevPname = i > 0 ? arr[i - 1]._source_project_name : null;
                  const showProjectHeader = selectedProject === ALL_ISRAEL
                    && robot._source_project_name
                    && robot._source_project_name !== prevPname;
                  const sevColor = robot.top_severity === 'red'
                    ? SEV_COLORS.critical
                    : robot.top_severity === 'yellow'
                      ? SEV_COLORS.high
                      : SEV_COLORS.normal;
                  // v1.10.45 — compute row's tier for conditional PINK rendering + parking-outline badge.
                  const _tierOfV45 = (rr) => {
                    const k = rr.last_cycle_kind;
                    if (k === 'real') return 1;
                    if (k === 'obstacle_only') return 2;
                    if (rr.last_error_cycle_index === 2) return 2;
                    return 3;
                  };
                  const _rowTier = _tierOfV45(robot);
                  const _prevRowTier = i > 0 ? _tierOfV45(arr[i - 1]) : null;
                  const _showTierDivider = sortMode === 'status_first'
                    && selectedProject !== ALL_ISRAEL
                    && i > 0
                    && _prevRowTier !== _rowTier;
                  const _isParkT1 = _rowTier === 1
                    && !robot.currently_on_error
                    && String(robot.current_status || '').toLowerCase() === 'parking';

                  return (
                    <React.Fragment key={`${robot._source_project_id || ''}-${robot.robot_number || i}`}>
                      {/* v1.10.45d — tier divider row when the sort group changes. */}
                      {_showTierDivider && (
                        <tr style={{ background: '#f1f3f5' }}>
                          <td colSpan={6} style={{
                            padding: '8px 14px',
                            borderTop: '4px solid #2c3e50',
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#2c3e50',
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                          }}>
                            ── Tier {_rowTier} — {_rowTier === 1
                              ? 'Real error in last cycle (top priority)'
                              : _rowTier === 2
                                ? 'Obstacle-only in last cycle, or real error 2 cycles ago'
                                : 'Older / no recent real error'} ──
                          </td>
                        </tr>
                      )}
                      {showProjectHeader && (
                        <tr style={{ background: '#eef2f7' }}>
                          <td colSpan={6} style={{
                            padding: '10px 14px',
                            fontSize: 13, fontWeight: 700,
                            color: '#1e3a8a', letterSpacing: 0.3,
                            borderTop: '2px solid #c7d2fe',
                            borderBottom: '1px solid #cbd5e1',
                          }}>
                            🇮🇱 {robot._source_project_name}
                          </td>
                        </tr>
                      )}
                    <tr
                      style={styles.robotRow}
                    >
                      {/* Robot # (or Repeater #) */}
                      <td style={styles.robotCell}>
                        <span style={styles.robotNum}>
                          {robot.kind === 'repeater' ? 'R' : '#'}{robot.robot_number}
                        </span>
                        {robot.kind === 'repeater' && (
                          <div style={{ fontSize: 10, color: '#d63031', marginTop: 2, fontWeight: 600 }}>
                            REPEATER
                          </div>
                        )}
                        {robot.kind !== 'repeater' && robot.risk_score > 0 && (
                          <div style={{ fontSize: 10, color: '#b2bec3', marginTop: 2 }}>
                            risk: {robot.risk_score}
                          </div>
                        )}
                      </td>

                      {/* Error column — v1.6.1 (rules 1+2+3, "current"
                          word removed). Switch on error_display_mode:
                            'current'    → status badge + Y/R code + severity
                                           + "Xh ago" (when it started)
                            'historical' → status badge + Y/R code + age tag
                                           ONLY (no severity word — not in
                                           error right now)
                            'comms_only' → status badge + "Comms — N events
                                           / 7d" (no Y/R error)
                            'none'       → status badge only (kept by
                                           is_inactive fallback) */}
                      <td style={styles.robotCell}>
                        <div style={styles.statusTop}>
                          <StatusBadge status={robot.current_status} t1ParkingFlag={_isParkT1} />
                        </div>
                        {/* v1.10.45 — PINK box error details only render for T1 rows; remove the error code name. */}
                        {/* v1.10.50 — PINK box: important error in the LAST cycle only
                            (last_cycle_important_*). Blank when the last cycle had none.
                            Rendered for T1 rows in current/historical mode. */}
                        {_rowTier === 1
                          && (robot.error_display_mode === 'current' || robot.error_display_mode === 'historical')
                          && robot.last_cycle_important_error && (
                          <>
                            <div style={styles.errorCode}>
                              {errorWithLocation(
                                robot.last_cycle_important_error,
                                robot.last_cycle_important_row,
                                robot.last_cycle_important_location
                              )}
                            </div>
                            <div style={styles.sevSmall(robot.last_cycle_important_severity)}>
                              {robot.last_cycle_important_severity === 'red' ? 'CRITICAL' : 'WARNING'}
                              {robot.last_cycle_important_time && (
                                <span style={{ color: '#b2bec3', marginLeft: 6 }}>
                                  · {timeAgo(robot.last_cycle_important_time)} ago
                                </span>
                              )}
                            </div>
                          </>
                        )}
                        {robot.error_display_mode === 'comms_only' && (
                          <div style={{ color: '#7f8c8d', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                            Comms — {robot.comms_count_7d} events / 7d
                          </div>
                        )}
                      </td>

                      {/* Issue (RCA) — short analysis. Robots: clickable to Robot Summary.
                          Repeaters: no Smart Analysis link, just hover for history. */}
                      <td
                        style={{
                          ...styles.robotCell,
                          cursor: robot.kind === 'repeater' ? 'default' : 'pointer',
                        }}
                        onMouseEnter={e => setTooltip({ visible: true, robot, x: e.clientX, y: e.clientY })}
                        onMouseMove={e => setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }))}
                        onMouseLeave={() => setTooltip({ visible: false, robot: null, x: 0, y: 0 })}
                        onClick={() => {
                          if (robot.kind === 'repeater') return;
                          // v1.10.4 Amendment J — pass return_anchor so the
                          // Robot Summary "← Return to Assignment Page"
                          // button can scroll back to this robot's row.
                          // showAll preserves the filter state on return.
                          const params = new URLSearchParams({
                            project: selectedProject,
                            robot: String(robot.robot_number),
                            from: 'assignment',
                            return_anchor: `robot-${robot.robot_number}`,
                          });
                          if (showAll) params.set('return_filter', 'show_all');
                          navigate(`/robot-summary?${params.toString()}`);
                        }}
                      >
                        {robot.is_inactive && (
                          <div
                            style={{
                              ...styles.inactivePrefix,
                              // v1.5.2: colour by mode — red for Airtouch
                              // (most severe — robot disabled by us), orange
                              // for Client (disabled by customer), purple
                              // for portable, gray for generic inactive.
                              color:
                                robot.inactive_mode === 'inactiveAirtouch' ? '#d63031' :
                                robot.inactive_mode === 'inactiveClient'   ? '#e67e22' :
                                robot.inactive_mode === 'portable'         ? '#6c5ce7' :
                                                                             '#636e72',
                            }}
                            title={`operationMode = ${robot.inactive_mode || 'inactive'}`}
                          >
                            Inactive
                            {robot.inactive_mode && robot.inactive_mode !== 'inactive'
                              ? ` (${robot.inactive_mode.replace(/^inactive/i,'')})` : ''}
                            {' — '}
                            {robot.inactive_since_days == null
                              ? 'no recent activity'
                              : robot.inactive_since_days === 0
                                ? 'last active today'
                                : robot.inactive_capped
                                  ? `since last ${robot.inactive_since_days}+ days`
                                  : `since last ${robot.inactive_since_days} day${robot.inactive_since_days === 1 ? '' : 's'}`}
                          </div>
                        )}
                        {/* Smart Analysis short text — 3 lines from cache.
                            Repeaters use their inline-built analysis_text and
                            get a purple "REPEATER" tag (v1.6.1, rule 1.1) so
                            the tech sees at a glance it's not a robot row. */}
                        {robot.kind === 'repeater' ? (
                          <div style={styles.rcaMeta}>
                            <div style={{
                              color: '#6c5ce7',
                              fontWeight: 800,
                              fontSize: 11,
                              letterSpacing: 0.5,
                              textTransform: 'uppercase',
                              marginBottom: 3,
                            }}>
                              Repeater
                            </div>
                            {robot.analysis_text || 'Repeater unresponsive'}
                          </div>
                        ) : (
                          <RcaCell
                            robot={robot}
                            onForceRefresh={(e) => {
                              e.stopPropagation();
                              handleForceRcaRefresh(robot.robot_number);
                            }}
                          />
                        )}
                        <div style={styles.hoverHint}>
                          {robot.kind === 'repeater'
                            ? 'hover for history'
                            : 'click for full Smart Analysis · hover for history'}
                        </div>
                      </td>

                      {/* Assign technician */}
                      <td style={styles.robotCell}>
                        {!assignments[robot.robot_number] && returnedRobots[robot.robot_number] && (
                          <div
                            style={styles.failToFixChip}
                            title={returnedRobots[robot.robot_number].reason_text || ''}
                          >
                            ↩ Fail to fix
                            {returnedRobots[robot.robot_number].reason_code &&
                              ` · ${REASON_CODE_LABEL[returnedRobots[robot.robot_number].reason_code] || returnedRobots[robot.robot_number].reason_code}`}
                          </div>
                        )}
                        {assignments[robot.robot_number] ? (
                          <div style={styles.assignedBadge}>
                            {assignments[robot.robot_number]}
                          </div>
                        ) : (
                          <select
                            style={styles.assignSelect}
                            value=""
                            onChange={e => {
                              const techId = e.target.value;
                              if (!techId) return;
                              const tech = projectTechnicians.find(t => t.id === techId) || allTechnicians.find(t => t.id === techId);
                              const techName = tech?.fullName || techId;

                              assignRobotToTechnician({
                                project_id: selectedProject,
                                project_name: selectedProjectName,
                                robot_number: robot.robot_number,
                                technician_id: techId,
                                technician_name: techName,
                                risk_level: robot.top_severity || 'high',
                                top_error: robot.top_error || '',
                                faults_7d: robot.total_faults || 0,
                                factors: robot.factors || [],
                                manager_note: managerNotes[robot.robot_number] || '',
                              }).then(() => {
                                setAssignments(prev => ({
                                  ...prev,
                                  [robot.robot_number]: techName,
                                }));
                                setActionFeedback(prev => ({
                                  ...prev,
                                  [`assign_${robot.robot_number}`]: `Assigned to ${techName}`,
                                }));
                              }).catch(err => {
                                console.error('Assignment failed:', err);
                                setActionFeedback(prev => ({
                                  ...prev,
                                  [`assign_${robot.robot_number}`]: 'Assignment failed!',
                                }));
                              });
                            }}
                          >
                            <option value="">-- Assign --</option>
                            {projectTechnicians.map(t => {
                              // Build a compact label that shows role + plant
                              // for context when multiple people cover the site.
                              const parts = [t.fullName];
                              if (t.role && t.role.toLowerCase() !== 'technician') {
                                parts.push(`· ${t.role}`);
                              }
                              if (t.plantName) {
                                parts.push(`· ${t.plantName}`);
                              }
                              return (
                                <option key={t.id} value={t.id}>
                                  {parts.join(' ')}
                                </option>
                              );
                            })}
                          </select>
                        )}
                        {actionFeedback[`assign_${robot.robot_number}`] && (
                          <div style={styles.assignFeedback}>
                            {actionFeedback[`assign_${robot.robot_number}`]}
                          </div>
                        )}
                      </td>

                      {/* Manager Note */}
                      <td style={styles.robotCell}>
                        <textarea
                          style={styles.managerNote}
                          placeholder="הערה לטכנאי..."
                          value={managerNotes[robot.robot_number] || ''}
                          onChange={e => setManagerNotes(prev => ({
                            ...prev,
                            [robot.robot_number]: e.target.value,
                          }))}
                          rows={2}
                        />
                      </td>

                      {/* Action buttons */}
                      <td style={styles.robotCell}>
                        {(() => {
                          const st = rc[robot.robot_number] || {};
                          const busy = st.locked || !!st.sending;
                          const mkBtn = (key, label, emoji, baseStyle, onClick) => {
                            const sending = st.sending === key;
                            const acked = st.locked && st.lastEvent === key;
                            return (
                              <button
                                key={key}
                                style={{ ...styles.actionBtn, ...baseStyle, opacity: busy ? 0.45 : 1, cursor: busy ? 'default' : 'pointer' }}
                                disabled={busy}
                                onClick={onClick}
                                title={label}
                              >
                                {sending ? '📤' : acked ? `✓ ${st.countdown}s` : `${emoji} ${label}`}
                              </button>
                            );
                          };
                          return (
                            <div style={styles.actionRow}>
                              {mkBtn('START_CLEANING', 'Start', '▶', styles.actionActivate, () => rcDoCommand(robot, 'START_CLEANING'))}
                              {mkBtn('GO_HOME', 'Home', '🏠', styles.actionHome, () => rcDoCommand(robot, 'GO_HOME'))}
                              {mkBtn('RESET', 'Reset', '⟲', styles.actionReset, () => rcDoCommand(robot, 'RESET'))}
                              {mkBtn('BATTERY', 'Battery', '🔋', styles.actionCheck, () => rcDoBattery(robot))}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                    {rc[robot.robot_number]?.panelOpen && (() => {
                      const st = rc[robot.robot_number] || {};
                      const b = st.batt;
                      return (
                        <tr key={`${robot.robot_number}-ctrl`}>
                          <td colSpan={2} style={{ borderTop: 'none' }}></td>
                          <td colSpan={4} style={{ padding: '4px 14px 14px 0', verticalAlign: 'top' }}>
                            <div style={{ padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                              {st.line && (
                                <div style={{ fontSize: 13, fontWeight: 600, color: st.failed ? '#c0392b' : '#1e293b', marginBottom: b ? 8 : 0 }}>
                                  {st.line}
                                </div>
                              )}
                              {b && (
                                <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.7 }}>
                                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Battery — Robot {robot.robot_number}</div>
                                  {b.loading && <div>Now &nbsp;…&nbsp; refreshing…</div>}
                                  {b.offline && <div style={{ color: '#c0392b' }}>Now &nbsp;—&nbsp; robot offline, can't read battery</div>}
                                  {b.empty && <div style={{ color: '#b7791f' }}>Now &nbsp;—&nbsp; no reading yet, try again</div>}
                                  {b.error && <div style={{ color: '#c0392b' }}>Now &nbsp;—&nbsp; {b.message || 'could not read battery'}</div>}
                                  {b.reading && (
                                    <div style={{ fontWeight: 600 }}>
                                      Now &nbsp; {rcNowStr()} &nbsp; Voltage {b.reading.voltage != null ? b.reading.voltage : '—'} V · SOC {b.reading.soc != null ? b.reading.soc : '—'} % · Charging {b.reading.charging_current_a != null ? b.reading.charging_current_a : '—'} A
                                    </div>
                                  )}
                                  {b.history && b.history.map((h, hi) => (
                                    <div key={hi} style={{ color: '#64748b' }}>
                                      {h.ts ? new Date(h.ts).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'} &nbsp; Voltage {h.voltage != null ? h.voltage : '—'} V · SOC {h.soc != null ? h.soc : '—'} % · Charging {h.charging_current_a != null ? h.charging_current_a : '—'} A
                                    </div>
                                  ))}
                                </div>
                              )}
                              <button
                                onClick={() => rcSet(robot.robot_number, { panelOpen: false })}
                                style={{ marginTop: 8, fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                              >
                                close
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      </>}

      {/* Tooltip overlay */}
      <RobotTooltip visible={tooltip.visible} robot={tooltip.robot} x={tooltip.x} y={tooltip.y} />
    </div>
  );
}

/**
 * AssignedTasks sub-page — shows tasks assigned to technicians with status
 */
// ─── v1.10.18 — Tracking tab: 3-zone assignment lifecycle view ──────────
function TrackingView({ selectedProject, allIsrael, israelProjectIds }) {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [zonesOpen, setZonesOpen] = useState({ active: true, pending: true, closed: true });

  const load = useCallback(async () => {
    try {
      let raw;
      if (allIsrael) {
        raw = await listAssignments({ limit: 500 });
        if (Array.isArray(raw)) {
          const ilSet = new Set(israelProjectIds || []);
          raw = raw.filter(r => ilSet.has(r.project_id));
        }
      } else if (selectedProject) {
        raw = await listAssignments({ project_id: selectedProject, limit: 300 });
      } else {
        raw = [];
      }
      setRows(Array.isArray(raw) ? raw : []);
    } catch (_e) { /* keep existing rows */ }
  }, [selectedProject, allIsrael, israelProjectIds]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Latest row per (project, robot)
  const latest = useMemo(() => {
    const sorted = [...rows].sort((a, b) => (b.assigned_at || 0) - (a.assigned_at || 0));
    const seen = new Set();
    const out = [];
    for (const r of sorted) {
      const key = (r.project_id || '') + '#' + r.robot_number;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  }, [rows]);

  const now = Date.now();
  const THREE_DAYS = 3 * 86400 * 1000;
  const stOf = (r) => (r.status || '').toLowerCase();
  const active = latest.filter(r => ['open', 'in_progress', 'pulled'].includes(stOf(r)));
  const pending = latest.filter(r => stOf(r) === 'pending_review');
  const closed = latest.filter(r => stOf(r) === 'completed'
    && r.completed_at && (now - r.completed_at) <= THREE_DAYS);

  const ago = (ms) => {
    if (!ms) return '';
    const h = Math.floor((now - ms) / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return h + 'h ago';
    return Math.floor(h / 24) + 'd ago';
  };
  const fmtParts = (rawA, rawB) => {
    const raw = rawA || rawB;
    if (!raw) return '—';
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) {
        return arr.map(p => (typeof p === 'object'
          ? ((p.qty || p.qty_installed || 1) + '× ' + (p.part_id || ''))
          : String(p))).join(', ');
      }
      if (Array.isArray(arr)) return '—';
    } catch (_e) { /* not json - raw text */ }
    return String(raw);
  };
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));
  const toggleZone = (z) => setZonesOpen(p => ({ ...p, [z]: !p[z] }));

  const S = {
    zone: { marginBottom: 18 },
    zoneHead: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
      padding: '8px 12px', borderRadius: 6, fontWeight: 700, fontSize: 14 },
    projHead: { fontSize: 12, fontWeight: 700, color: '#555', margin: '12px 0 4px',
      borderBottom: '1px solid #e0e0e0', paddingBottom: 3 },
    row: { background: '#fff', border: '1px solid #e3e3e3', borderRadius: 6,
      padding: '8px 12px', marginBottom: 6 },
    rowMain: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    robot: { fontWeight: 700, minWidth: 54 },
    tech: { color: '#333' },
    badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 },
    verdict: { color: '#666', fontSize: 12, flex: 1, minWidth: 120 },
    when: { color: '#999', fontSize: 11 },
    fb: { marginTop: 8, paddingTop: 8, borderTop: '1px dashed #ddd' },
    fbItem: { marginBottom: 6 },
    fbLabel: { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' },
    fbText: { fontSize: 13, color: '#222', whiteSpace: 'pre-wrap' },
    meta: { fontSize: 11, color: '#999', marginTop: 4 },
    empty: { color: '#999', fontSize: 12, fontStyle: 'italic', padding: '6px 12px' },
  };

  const renderRow = (r, kind) => {
    const isOpen = !!expanded[r.id];
    return (
      <div key={r.id} style={S.row}>
        <div style={{ ...S.rowMain, cursor: kind === 'closed' ? 'pointer' : 'default' }}
             onClick={kind === 'closed' ? () => toggle(r.id) : undefined}>
          <span style={S.robot}>R-{r.robot_number}</span>
          <span style={S.tech}>{r.technician_name || r.technician_id || '—'}</span>
          {kind === 'active' && (
            <span style={{ ...S.badge, background: '#e3f2fd', color: '#1565c0' }}>
              {stOf(r) === 'open' ? 'Assigned' : 'In progress'}
            </span>
          )}
          {kind === 'pending' && (
            <span style={{ ...S.badge, background: '#fff8e1', color: '#f57f17' }}>
              {'✓'} Done {'—'} awaiting tech end-of-day confirmation
            </span>
          )}
          {kind === 'closed' && (
            <span style={{ ...S.badge, background: '#eceff1', color: '#455a64' }}>Closed</span>
          )}
          {kind === 'active' && <span style={S.verdict}>{r.error_description || ''}</span>}
          <span style={S.when}>
            {kind === 'closed' ? ago(r.completed_at) : ago(r.assigned_at)}
          </span>
          {kind === 'closed' && <span style={{ color: '#888' }}>{isOpen ? '▾' : '▸'}</span>}
        </div>
        {kind === 'closed' && isOpen && (
          <div style={S.fb}>
            {[['Robot state', r.findings_q1],
              ['Problem found', r.findings_q2],
              ['What was done', r.real_fix_description]].map(([lbl, txt]) => (
              <div key={lbl} style={S.fbItem}>
                <div style={S.fbLabel}>{lbl}</div>
                <div style={S.fbText} dir="auto">{txt || '—'}</div>
              </div>
            ))}
            <div style={S.fbItem}>
              <div style={S.fbLabel}>Parts used</div>
              <div style={S.fbText} dir="auto">{fmtParts(r.parts_replaced, r.parts_delta_json)}</div>
            </div>
            <div style={S.meta}>
              {r.time_taken_minutes != null ? ('Time: ' + r.time_taken_minutes + ' min') : ''}
              {r.feedback_language ? (' · language: ' + r.feedback_language) : ''}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderZoneBody = (zoneRows, kind) => {
    if (!zoneRows.length) return <div style={S.empty}>None.</div>;
    if (allIsrael) {
      const byProj = {};
      for (const r of zoneRows) {
        const k = r.project_id || '?';
        (byProj[k] = byProj[k] || []).push(r);
      }
      return Object.entries(byProj).map(([pid, rs]) => (
        <div key={pid}>
          <div style={S.projHead}>{rs[0].project_name || pid}</div>
          {rs.map(r => renderRow(r, kind))}
        </div>
      ));
    }
    return zoneRows.map(r => renderRow(r, kind));
  };

  const zone = (key, title, headBg, headColor, zoneRows, kind) => (
    <div style={S.zone}>
      <div style={{ ...S.zoneHead, background: headBg, color: headColor }}
           onClick={() => toggleZone(key)}>
        <span>{zonesOpen[key] ? '▾' : '▸'}</span>
        <span>{title}</span>
        <span style={{ ...S.badge, background: '#fff', color: headColor }}>{zoneRows.length}</span>
      </div>
      {zonesOpen[key] && <div style={{ marginTop: 6 }}>{renderZoneBody(zoneRows, kind)}</div>}
    </div>
  );

  if (!selectedProject) {
    return <div style={S.empty}>Select a project to see tracking.</div>;
  }
  return (
    <div style={{ padding: '12px 0' }}>
      {zone('active', 'Active — assigned & in progress', '#e8f5e9', '#2e7d32', active, 'active')}
      {zone('pending', 'Done — pending review', '#fff8e1', '#f57f17', pending, 'pending')}
      {zone('closed', 'Closed — last 3 days', '#eceff1', '#455a64', closed, 'closed')}
    </div>
  );
}


function AssignedTasksView() {
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'project' | 'status'
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'open' | 'closed'

  // Detect country from browser language
  const browserLang = navigator.language || 'en';
  const defaultCountry = browserLang.startsWith('he') ? 'Israel' : browserLang.startsWith('hi') ? 'India' : '';
  const [country, setCountry] = useState(defaultCountry);

  const { data, loading } = useAPI(
    () => getManagerAssignments(country),
    [country]
  );

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading assignments...</div>;

  const allAssignments = data?.assignments || [];

  // Get unique project names for filter
  const projectNames = [...new Set(allAssignments.map(a => a.project_name))].sort();

  // Apply filters
  let filtered = allAssignments;
  if (filterProject) {
    filtered = filtered.filter(a => a.project_name === filterProject);
  }
  if (filterStatus === 'open') {
    filtered = filtered.filter(a => a.is_open);
  } else if (filterStatus === 'closed') {
    filtered = filtered.filter(a => !a.is_open);
  }

  // Apply sort
  if (sortBy === 'project') {
    filtered.sort((a, b) => a.project_name.localeCompare(b.project_name));
  } else if (sortBy === 'status') {
    filtered.sort((a, b) => (a.is_open === b.is_open ? 0 : a.is_open ? -1 : 1));
  } else {
    // date — oldest first
    filtered.sort((a, b) => (a.assigned_at || '').localeCompare(b.assigned_at || ''));
  }

  const STATUS_COLORS = {
    pending: { bg: '#fff3cd', text: '#d68910', label: 'Pending' },
    assigned: { bg: '#e8f4fd', text: '#2980b9', label: 'Assigned' },
    en_route: { bg: '#e8f4fd', text: '#2980b9', label: 'En Route' },
    in_progress: { bg: '#fef3e2', text: '#e67e22', label: 'In Progress' },
    completed: { bg: '#d4edda', text: '#27ae60', label: 'Completed' },
    escalated: { bg: '#ffe0e0', text: '#c0392b', label: 'Escalated' },
    skipped: { bg: '#e9ecef', text: '#636e72', label: 'Skipped' },
  };

  const isHebrew = browserLang.startsWith('he');

  return (
    <div>
      {/* Country + filters bar */}
      <div style={assignedStyles.filtersBar}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={country} onChange={e => setCountry(e.target.value)} style={assignedStyles.filterSelect}>
            <option value="">{isHebrew ? 'כל המדינות' : 'All Countries'}</option>
            <option value="Israel">🇮🇱 {isHebrew ? 'ישראל' : 'Israel'}</option>
            <option value="India">🇮🇳 {isHebrew ? 'הודו' : 'India'}</option>
          </select>
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={assignedStyles.filterSelect}>
            <option value="">{isHebrew ? 'כל הפרויקטים' : 'All Projects'}</option>
            {projectNames.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={assignedStyles.filterSelect}>
            <option value="all">{isHebrew ? 'כל הסטטוסים' : 'All Statuses'}</option>
            <option value="open">{isHebrew ? 'פתוח' : 'Open'}</option>
            <option value="closed">{isHebrew ? 'סגור' : 'Closed'}</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#999' }}>{isHebrew ? 'מיון:' : 'Sort:'}</span>
          {[
            { key: 'date', label: isHebrew ? 'תאריך' : 'Date (oldest)' },
            { key: 'project', label: isHebrew ? 'פרויקט' : 'Project' },
            { key: 'status', label: isHebrew ? 'סטטוס' : 'Status' },
          ].map(s => (
            <button key={s.key} onClick={() => setSortBy(s.key)}
              style={{
                ...assignedStyles.sortBtn,
                ...(sortBy === s.key ? assignedStyles.sortBtnActive : {}),
              }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, margin: '16px 0' }}>
        <StatCard label={isHebrew ? 'סה"כ משימות' : 'Total Tasks'} value={data?.total || 0} color="#6c5ce7" />
        <StatCard label={isHebrew ? 'פתוחות' : 'Open'} value={data?.open || 0} color="#e17055" />
        <StatCard label={isHebrew ? 'סגורות' : 'Closed'} value={data?.closed || 0} color="#00b894" />
      </div>

      {/* Assignments table */}
      <div style={assignedStyles.tableWrapper}>
        <table style={assignedStyles.table}>
          <thead>
            <tr style={assignedStyles.thead}>
              <th style={assignedStyles.th}>{isHebrew ? 'רובוט' : 'Robot'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'תקלה' : 'Error'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'זמן מאז פתיחה' : 'Open Since'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'היסטוריית תקלות' : 'Error History'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'הנחיית מנהל' : 'Manager Note'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'ממצאי טכנאי' : 'Tech Findings'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'טכנאי' : 'Technician'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'סטטוס' : 'Status'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'תאריך טיפול' : 'Date'}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                  {isHebrew ? 'אין משימות משויכות' : 'No assigned tasks'}
                </td>
              </tr>
            )}
            {filtered.map((task, i) => {
              const sc = STATUS_COLORS[task.status] || STATUS_COLORS.pending;
              const daysSince = task.assigned_at
                ? Math.floor((Date.now() - new Date(task.assigned_at).getTime()) / 86400000)
                : null;

              return (
                <tr key={task.task_id || i} style={{
                  ...assignedStyles.row,
                  background: task.is_open ? '#fff' : '#fafafa',
                  opacity: task.is_open ? 1 : 0.8,
                }}>
                  {/* Robot # + Project */}
                  <td style={assignedStyles.td}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>#{task.robot_number}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>{task.project_name}</div>
                  </td>

                  {/* Error */}
                  <td style={assignedStyles.td}>
                    <SeverityBadge severity={task.severity === 'red' ? 'RED' : task.severity === 'orange' ? 'YELLOW' : 'YELLOW'} />
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#6c5ce7', marginTop: 4 }}>
                      {task.primary_error?.replace(/_/g, ' ')}
                    </div>
                  </td>

                  {/* Days since opened */}
                  <td style={assignedStyles.td}>
                    {daysSince !== null ? (
                      <span style={{
                        fontWeight: 600,
                        color: daysSince > 3 ? '#d63031' : daysSince > 1 ? '#e17055' : '#636e72',
                      }}>
                        {daysSince === 0
                          ? (isHebrew ? 'היום' : 'Today')
                          : daysSince === 1
                            ? (isHebrew ? 'אתמול' : '1 day')
                            : `${daysSince} ${isHebrew ? 'ימים' : 'days'}`}
                      </span>
                    ) : '-'}
                  </td>

                  {/* Error history */}
                  <td style={assignedStyles.td}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {task.error_codes?.slice(0, 4).map((code, j) => {
                        const eh = task.error_history?.find(e => e.code === code);
                        const sev = eh?.severity || 'yellow';
                        return (
                          <span key={j} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: 10, padding: '1px 5px', borderRadius: 3,
                            background: sev === 'red' ? '#ffe0e0' : sev === 'green' ? '#d4edda' : '#fff3cd',
                          }}>
                            <span style={{
                              width: 5, height: 5, borderRadius: '50%',
                              background: sev === 'red' ? '#c0392b' : sev === 'green' ? '#27ae60' : '#d68910',
                            }}/>
                            {humanizeCode(code).substring(0, 20)}
                          </span>
                        );
                      })}
                      {(task.error_codes?.length || 0) > 4 && (
                        <span style={{ fontSize: 10, color: '#999' }}>+{task.error_codes.length - 4}</span>
                      )}
                    </div>
                  </td>

                  {/* Manager note */}
                  <td style={{ ...assignedStyles.td, maxWidth: 160, direction: 'rtl' }}>
                    <div style={{ fontSize: 11, color: '#444', lineHeight: 1.4 }}>
                      {task.manager_note || '-'}
                    </div>
                  </td>

                  {/* Technician findings */}
                  <td style={{ ...assignedStyles.td, maxWidth: 160, direction: 'rtl' }}>
                    {task.technician_findings || task.technician_notes ? (
                      <div style={{ fontSize: 11, color: '#2d3436', lineHeight: 1.4 }}>
                        {task.technician_findings || task.technician_notes}
                        {task.parts_replaced?.length > 0 && (
                          <div style={{ marginTop: 3, fontSize: 10, color: '#27ae60' }}>
                            {isHebrew ? 'חלקים:' : 'Parts:'} {task.parts_replaced.join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: '#b2bec3' }}>
                        {task.is_open ? (isHebrew ? 'ממתין לטכנאי' : 'Waiting for tech') : '-'}
                      </span>
                    )}
                  </td>

                  {/* Technician name */}
                  <td style={assignedStyles.td}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{task.assigned_to_name || task.assigned_to_id || '-'}</div>
                  </td>

                  {/* Status */}
                  <td style={assignedStyles.td}>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      background: sc.bg,
                      color: sc.text,
                    }}>
                      {sc.label}
                    </span>
                  </td>

                  {/* Treatment date — only shown after technician completes */}
                  <td style={assignedStyles.td}>
                    <div style={{ fontSize: 11, color: '#636e72' }}>
                      {task.completed_at
                        ? new Date(task.completed_at).toLocaleDateString()
                        : <span style={{ color: '#b2bec3' }}>-</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * ClosedTickets sub-page — shows resolved/escalated tickets with full resolution data
 */
function ClosedTicketsView() {
  const [expandedTicket, setExpandedTicket] = useState(null);

  const browserLang = navigator.language || 'en';
  const defaultCountry = browserLang.startsWith('he') ? 'Israel' : browserLang.startsWith('hi') ? 'India' : '';
  const [country, setCountry] = useState(defaultCountry);

  const { data, loading } = useAPI(
    () => getClosedTickets(country),
    [country]
  );

  const isHebrew = browserLang.startsWith('he');

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading closed tickets...</div>;

  const tickets = data?.tickets || [];

  const STATUS_COLORS = {
    resolved: { bg: '#d4edda', text: '#27ae60', label: isHebrew ? 'טופל' : 'Resolved' },
    escalated: { bg: '#ffe0e0', text: '#c0392b', label: isHebrew ? 'הועבר' : 'Escalated' },
  };

  return (
    <div>
      {/* Filters */}
      <div style={assignedStyles.filtersBar}>
        <select value={country} onChange={e => setCountry(e.target.value)} style={assignedStyles.filterSelect}>
          <option value="">{isHebrew ? 'כל המדינות' : 'All Countries'}</option>
          <option value="Israel">🇮🇱 {isHebrew ? 'ישראל' : 'Israel'}</option>
          <option value="India">🇮🇳 {isHebrew ? 'הודו' : 'India'}</option>
        </select>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, margin: '16px 0' }}>
        <StatCard label={isHebrew ? 'סה"כ טיקטים' : 'Total Tickets'} value={data?.total || 0} color="#6c5ce7" />
        <StatCard label={isHebrew ? 'טופלו' : 'Resolved'} value={data?.resolved || 0} color="#00b894" />
        <StatCard label={isHebrew ? 'הועברו' : 'Escalated'} value={data?.escalated || 0} color="#d63031" />
        <StatCard label={isHebrew ? 'זמן תיקון ממוצע' : 'Avg Repair'} value={`${data?.avg_repair_minutes || 0}m`} color="#0984e3" />
        <StatCard label={isHebrew ? 'תקלות חוזרות' : 'Repeat Errors'} value={data?.repeat_errors || 0} color="#e17055" />
        <StatCard label={isHebrew ? 'דיוק חיזוי' : 'Prediction Accuracy'} value={`${data?.prediction_accuracy || 0}%`} color="#00cec9" />
      </div>

      {/* Tickets table */}
      <div style={assignedStyles.tableWrapper}>
        <table style={{ ...assignedStyles.table, minWidth: 1200 }}>
          <thead>
            <tr style={assignedStyles.thead}>
              <th style={assignedStyles.th}>{isHebrew ? 'רובוט' : 'Robot'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'תקלה' : 'Error'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'טכנאי' : 'Technician'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'ממצאים' : 'Findings'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'תיקון' : 'Fix'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'חלקים' : 'Parts'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'זמן תיקון' : 'Repair Time'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'זמן תגובה' : 'Response'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'דגלים' : 'Flags'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'סטטוס' : 'Status'}</th>
              <th style={assignedStyles.th}>{isHebrew ? 'תאריך' : 'Date'}</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                  {isHebrew ? 'אין טיקטים סגורים עדיין' : 'No closed tickets yet'}
                </td>
              </tr>
            )}
            {tickets.map((ticket, i) => {
              const sc = STATUS_COLORS[ticket.status] || STATUS_COLORS.resolved;
              const isExpanded = expandedTicket === ticket.ticket_id;

              return (
                <React.Fragment key={ticket.ticket_id || i}>
                  <tr
                    style={{ ...assignedStyles.row, cursor: 'pointer' }}
                    onClick={() => setExpandedTicket(isExpanded ? null : ticket.ticket_id)}
                  >
                    {/* Robot + Project */}
                    <td style={assignedStyles.td}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>#{ticket.robot_number}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{ticket.project_name}</div>
                    </td>

                    {/* Error */}
                    <td style={assignedStyles.td}>
                      <SeverityBadge severity={ticket.severity === 'red' ? 'RED' : 'YELLOW'} />
                      <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#6c5ce7', marginTop: 4 }}>
                        {ticket.primary_error?.replace(/_/g, ' ')}
                      </div>
                    </td>

                    {/* Technician */}
                    <td style={assignedStyles.td}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{ticket.technician_name || ticket.technician_id}</div>
                    </td>

                    {/* Findings */}
                    <td style={{ ...assignedStyles.td, maxWidth: 160, direction: 'rtl' }}>
                      <div style={{ fontSize: 11, color: '#2d3436', lineHeight: 1.4 }}>
                        {ticket.findings || '-'}
                      </div>
                    </td>

                    {/* Fix */}
                    <td style={{ ...assignedStyles.td, maxWidth: 160, direction: 'rtl' }}>
                      <div style={{ fontSize: 11, color: '#2d3436', lineHeight: 1.4 }}>
                        {ticket.fix_description || '-'}
                      </div>
                    </td>

                    {/* Parts */}
                    <td style={assignedStyles.td}>
                      {ticket.parts_replaced?.length > 0 ? (
                        <div style={{ fontSize: 11, color: '#27ae60' }}>
                          {ticket.parts_replaced.join(', ')}
                        </div>
                      ) : <span style={{ color: '#b2bec3', fontSize: 11 }}>-</span>}
                    </td>

                    {/* Repair duration */}
                    <td style={assignedStyles.td}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {ticket.repair_duration_minutes}m
                      </span>
                    </td>

                    {/* Response time */}
                    <td style={assignedStyles.td}>
                      <span style={{ fontSize: 12, color: '#636e72' }}>
                        {ticket.response_time_minutes}m
                      </span>
                    </td>

                    {/* Flags */}
                    <td style={assignedStyles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {ticket.repeat_error && (
                          <span style={closedStyles.flag} title={isHebrew ? 'תקלה חוזרת' : 'Repeat error'}>🔄</span>
                        )}
                        {ticket.previous_fix_failed && (
                          <span style={closedStyles.flag} title={isHebrew ? 'תיקון קודם נכשל' : 'Previous fix failed'}>⚠️</span>
                        )}
                        {ticket.is_repeat_location && (
                          <span style={closedStyles.flag} title={isHebrew ? 'מיקום חוזר' : 'Repeat location'}>📍</span>
                        )}
                        {ticket.weather_factor && (
                          <span style={closedStyles.flag} title={isHebrew ? 'גורם מזג אוויר' : 'Weather factor'}>🌡️</span>
                        )}
                        {ticket.matched_prediction === false && (
                          <span style={closedStyles.flag} title={isHebrew ? 'חיזוי שגוי' : 'Wrong prediction'}>❌</span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td style={assignedStyles.td}>
                      <span style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: 4,
                        fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.text,
                      }}>
                        {sc.label}
                      </span>
                    </td>

                    {/* Date */}
                    <td style={assignedStyles.td}>
                      <div style={{ fontSize: 11, color: '#636e72' }}>
                        {ticket.closed_at ? new Date(ticket.closed_at).toLocaleDateString() : '-'}
                      </div>
                      <div style={{ fontSize: 10, color: '#b2bec3' }}>
                        {ticket.closed_at ? new Date(ticket.closed_at).toLocaleTimeString() : ''}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr style={{ background: '#f8f9fa' }}>
                      <td colSpan={11} style={{ padding: 20 }}>
                        <div style={closedStyles.detailGrid}>
                          {/* Robot History */}
                          <div style={closedStyles.detailCard}>
                            <h4 style={closedStyles.detailTitle}>{isHebrew ? 'היסטוריית רובוט' : 'Robot History'}</h4>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'תקלות 90 יום:' : 'Errors 90d:'}</span>
                              <strong>{ticket.robot_history?.total_errors_90d || 0}</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'תקלות 7 ימים:' : 'Errors 7d:'}</span>
                              <strong>{ticket.robot_history?.total_errors_7d || 0}</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'תיקונים קודמים:' : 'Past repairs:'}</span>
                              <strong>{ticket.robot_history?.total_repairs || 0}</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'כרוני:' : 'Chronic:'}</span>
                              <strong>{ticket.robot_history?.is_chronic ? '✅' : '❌'}</strong>
                            </div>
                            {ticket.robot_history?.top_errors?.length > 0 && (
                              <div style={{ marginTop: 8, fontSize: 11 }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>{isHebrew ? 'תקלות נפוצות:' : 'Top errors:'}</div>
                                {ticket.robot_history.top_errors.map((e, j) => (
                                  <div key={j} style={{ color: '#636e72' }}>
                                    {humanizeCode(e.code)} ({e.count})
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* System Prediction */}
                          <div style={closedStyles.detailCard}>
                            <h4 style={closedStyles.detailTitle}>{isHebrew ? 'חיזוי מערכת' : 'System Prediction'}</h4>
                            <div style={{ fontSize: 11, color: '#2d3436', lineHeight: 1.5, direction: 'rtl' }}>
                              {ticket.system_brief}
                            </div>
                            {ticket.motor_enrichment && (
                              <div style={{ marginTop: 8, fontSize: 11, color: '#6c5ce7', direction: 'rtl' }}>
                                {ticket.motor_enrichment}
                              </div>
                            )}
                            {ticket.suggested_parts?.length > 0 && (
                              <div style={{ marginTop: 8, fontSize: 11 }}>
                                <span style={{ fontWeight: 600 }}>{isHebrew ? 'חלקים מוצעים: ' : 'Suggested parts: '}</span>
                                {ticket.suggested_parts.join(', ')}
                              </div>
                            )}
                            <div style={{ marginTop: 8 }}>
                              <span style={{
                                fontSize: 11, padding: '2px 6px', borderRadius: 3,
                                background: ticket.matched_prediction ? '#d4edda' : ticket.matched_prediction === false ? '#ffe0e0' : '#e9ecef',
                                color: ticket.matched_prediction ? '#27ae60' : ticket.matched_prediction === false ? '#c0392b' : '#636e72',
                              }}>
                                {ticket.matched_prediction ? (isHebrew ? '✓ חיזוי מדויק' : '✓ Prediction matched')
                                  : ticket.matched_prediction === false ? (isHebrew ? '✗ חיזוי שגוי' : '✗ Prediction wrong')
                                  : (isHebrew ? '- לא ידוע' : '- Unknown')}
                              </span>
                            </div>
                          </div>

                          {/* Timing */}
                          <div style={closedStyles.detailCard}>
                            <h4 style={closedStyles.detailTitle}>{isHebrew ? 'תזמון' : 'Timing'}</h4>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'שויכה:' : 'Assigned:'}</span>
                              <strong>{ticket.assigned_at ? new Date(ticket.assigned_at).toLocaleString() : '-'}</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'התחיל:' : 'Started:'}</span>
                              <strong>{ticket.started_at ? new Date(ticket.started_at).toLocaleString() : '-'}</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'סיים:' : 'Completed:'}</span>
                              <strong>{ticket.completed_at ? new Date(ticket.completed_at).toLocaleString() : '-'}</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'זמן תיקון:' : 'Repair time:'}</span>
                              <strong style={{ color: '#0984e3' }}>{ticket.repair_duration_minutes} min</strong>
                            </div>
                            <div style={closedStyles.detailRow}>
                              <span>{isHebrew ? 'זמן תגובה (שיוך → סיום):' : 'Response (assign → done):'}</span>
                              <strong>{ticket.response_time_minutes} min</strong>
                            </div>
                          </div>

                          {/* Technician Notes */}
                          {ticket.technician_notes && (
                            <div style={closedStyles.detailCard}>
                              <h4 style={closedStyles.detailTitle}>{isHebrew ? 'הערות טכנאי' : 'Tech Notes'}</h4>
                              <div style={{ fontSize: 12, color: '#2d3436', direction: 'rtl', lineHeight: 1.5 }}>
                                {ticket.technician_notes}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const closedStyles = {
  flag: {
    fontSize: 14,
    cursor: 'help',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
  },
  detailCard: {
    background: '#fff',
    borderRadius: 8,
    padding: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  detailTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#6c5ce7',
    marginTop: 0,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid #f0f0f0',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    padding: '4px 0',
    borderBottom: '1px solid #fafafa',
  },
};

const assignedStyles = {
  filtersBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  filterSelect: {
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 12,
    background: '#fff',
  },
  sortBtn: {
    padding: '4px 10px',
    borderRadius: 4,
    border: '1px solid #ddd',
    background: '#fff',
    fontSize: 11,
    cursor: 'pointer',
  },
  sortBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },
  tableWrapper: {
    background: '#fff',
    borderRadius: 12,
    overflowX: 'auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 1100 },
  thead: { background: '#f8f9fa' },
  th: {
    padding: '10px 10px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#636e72',
    textAlign: 'left',
    borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '10px 10px', verticalAlign: 'top', fontSize: 12 },
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: '#636e72', marginTop: 4 },

  tabBar: {
    display: 'flex',
    gap: 0,
    marginBottom: 20,
    borderBottom: '2px solid #eee',
  },
  tab: {
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 600,
    color: '#636e72',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
    marginBottom: -2,
  },
  tabActive: {
    color: '#6c5ce7',
    borderBottomColor: '#6c5ce7',
  },

  changeProjBtn: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid #6c5ce7',
    background: 'rgba(108,92,231,0.08)',
    color: '#6c5ce7',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },

  // --- Project Selector ---
  selectorPanel: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    maxHeight: 500,
    overflowY: 'auto',
  },
  selectorGroup: { marginBottom: 16 },
  selectorGroupTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e17055',
    textTransform: 'uppercase',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottom: '1px solid #f0f0f0',
  },
  recentRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  recentBtn: {
    padding: '6px 14px',
    borderRadius: 20,
    border: '1px solid #ddd',
    background: '#fafafa',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.1s',
  },
  recentBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },

  selectorCountry: {
    fontSize: 14,
    fontWeight: 700,
    padding: '8px 0 4px',
    color: '#1a1a2e',
  },
  selectorRegion: { marginLeft: 16, marginBottom: 8 },
  selectorRegionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6c5ce7',
    marginBottom: 4,
  },
  selectorProjects: { display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 8 },
  selectorProjectBtn: {
    padding: '4px 12px',
    borderRadius: 6,
    border: '1px solid #e0e0e0',
    background: '#fff',
    fontSize: 12,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  selectorProjectBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },
  selectorRobotCount: {
    fontSize: 10,
    opacity: 0.6,
  },

  // --- Cards ---
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '16px 0 24px',
  },
  cardGridTwo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
    margin: '0 0 24px',
  },

  // --- Error-cell restyle ---
  statusTop: { marginBottom: 4 },
  sevSmall: (sev) => ({
    fontSize: 10,
    marginTop: 4,
    letterSpacing: 0.3,
    color: sev === 'red' ? '#c0392b' : sev === 'yellow' ? '#d68910' : '#636e72',
    textTransform: 'uppercase',
    fontWeight: 600,
  }),

  // --- RCA cell ---
  inactivePrefix: {
    color: '#d63031',
    fontWeight: 700,
    fontSize: 12,
    marginBottom: 4,
    letterSpacing: 0.2,
  },
  rcaCellWrap: {
    display: 'flex', flexDirection: 'column', gap: 1,
  },
  rcaModeFlag: {
    fontSize: 11, fontWeight: 700, lineHeight: 1.35, marginBottom: 2,
  },
  rcaVerdict: {
    fontSize: 12, fontWeight: 700, color: '#2d3436', lineHeight: 1.35,
  },
  rcaBody: {
    fontSize: 11, color: '#636e72', lineHeight: 1.4,
  },
  rcaStatus: {
    fontSize: 10, color: '#95a5a6', lineHeight: 1.4, marginTop: 2,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  rcaBring: {
    fontSize: 11, color: '#0984e3', fontWeight: 600, lineHeight: 1.4,
  },
  rcaPlaceholder: {
    fontSize: 12, color: '#95a5a6', fontStyle: 'italic', lineHeight: 1.4,
  },
  freshDot: {
    display: 'inline-block', marginRight: 6, fontSize: 12, fontWeight: 700,
  },
  rcaRefreshBtn: {
    background: 'none', border: '1px solid #ecf0f1', borderRadius: 6,
    padding: '1px 6px', fontSize: 9, color: '#95a5a6', cursor: 'pointer',
    fontFamily: 'inherit', alignSelf: 'flex-start', marginTop: 2,
  },
  rcaLink: {
    fontSize: 12,
    color: '#2d3436',
    lineHeight: 1.4,
    marginBottom: 4,
    textDecoration: 'underline',
    textDecorationColor: '#cdd6e0',
    textUnderlineOffset: 3,
  },
  rcaMeta: {
    fontSize: 11,
    color: '#636e72',
    marginTop: 2,
  },

  // --- Robot Table ---
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },

  tableWrapper: {
    background: '#fff',
    borderRadius: 12,
    overflowX: 'auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  robotTable: { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
  robotTh: { background: '#f8f9fa' },
  thCell: {
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#636e72',
    textAlign: 'left',
    borderBottom: '2px solid #eee',
  },
  robotRow: { borderBottom: '1px solid #f5f5f5' },
  robotCell: { padding: '10px 12px', verticalAlign: 'top' },

  robotNum: { fontSize: 16, fontWeight: 700, color: '#2d3436' },

  severityBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  faultCode: { fontSize: 11, color: '#636e72', marginTop: 3, textTransform: 'capitalize' },

  errorCode: {
    fontSize: 10,
    fontFamily: 'monospace',
    color: '#6c5ce7',
    marginTop: 4,
    wordBreak: 'break-all',
  },
  errorNameHe: {
    fontSize: 11,
    color: '#636e72',
    marginTop: 2,
    direction: 'rtl',
    textAlign: 'right',
  },
  summaryText: { fontSize: 12, color: '#444', lineHeight: 1.4, marginBottom: 4 },
  historyList: { marginTop: 4, marginBottom: 4 },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    padding: '2px 0',
    color: '#444',
  },
  historyDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  historyCode: { fontSize: 11, color: '#2d3436' },
  historyTime: { fontSize: 10, color: '#b2bec3', marginLeft: 'auto' },
  repeatErrors: {
    fontSize: 10,
    color: '#e17055',
    marginTop: 3,
    fontStyle: 'italic',
  },
  hoverHint: {
    fontSize: 9,
    color: '#b2bec3',
    marginTop: 4,
    fontStyle: 'italic',
  },

  equipList: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  equipChip: {
    padding: '2px 8px',
    borderRadius: 4,
    background: '#e8f5e9',
    color: '#27ae60',
    fontSize: 10,
    fontWeight: 500,
  },

  managerNote: {
    width: '100%',
    maxWidth: 160,
    padding: '6px 8px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 12,
    fontFamily: 'inherit',
    resize: 'vertical',
    direction: 'rtl',
    lineHeight: 1.4,
    background: '#fafafa',
  },
  assignSelect: {
    padding: '6px 8px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 12,
    background: '#fff',
    cursor: 'pointer',
    width: '100%',
    maxWidth: 140,
  },

  actionRow: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  actionBtn: {
    padding: '4px 8px',
    borderRadius: 4,
    border: 'none',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.1s',
  },
  actionCheck: { background: '#dfe6e9', color: '#2d3436' },
  actionReset: { background: '#ffeaa7', color: '#d68910' },
  actionHome: { background: '#81ecec', color: '#00695c' },
  actionActivate: { background: '#a29bfe', color: '#fff' },

  feedbackMsg: {
    fontSize: 11,
    color: '#00b894',
    fontWeight: 600,
    fontStyle: 'italic',
  },
  failToFixChip: {
    display: 'inline-block',
    padding: '2px 8px',
    background: '#fff3e0',
    color: '#bf360c',
    border: '1px solid #ffab40',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 4,
    cursor: 'help',
  },
    assignedBadge: {
    padding: '4px 10px',
    borderRadius: 6,
    background: '#e8f5e9',
    color: '#27ae60',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'center',
  },
  assignFeedback: {
    fontSize: 10,
    color: '#00b894',
    marginTop: 4,
    fontWeight: 500,
  },

  // --- Empty state ---
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: '#fff',
    borderRadius: 12,
    marginTop: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  openSelectorBtn: {
    marginTop: 16,
    padding: '10px 24px',
    borderRadius: 8,
    border: 'none',
    background: '#6c5ce7',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
